<template>
  <BaseLayout :menuLinks="sideMenuLinks">
    <template v-slot>
      <div class="flex flex-column w-full">
        <!-- Modern Breadcrumb Navigation -->
        <ModernBreadcrumb :items="breadcrumbItems"/>

        <!-- Standard Header -->
        <StandardHeader
            :title="source?.name || 'Feed Details'"
            subtitle="Configure and explore your data source"
            icon="pi pi-chart-bar"
            :actions="headerActions"
        />

        <!-- Modern Tab View -->
        <TabView v-model:activeIndex="activeTabIndex" class="modern-tabs">

          <!-- Discovery Tab -->
          <TabPanel>
            <template #header>
              <div class="flex align-items-center">
                <i class="pi pi-compass mr-2"></i>
                <span>Discovery</span>
              </div>
            </template>

            <!-- État 1: Chargement en cours -->
            <div v-if="loading" class="flex flex-column align-items-center p-6">
              <i class="pi pi-spinner pi-spin text-4xl text-blue-500 mb-3"></i>
              <p class="text-gray-600">Loading feed...</p>
            </div>

            <!-- État 2: Erreur - source introuvable -->
            <Card v-else-if="!source" class="shadow-2 border-round-lg">
              <template #content>
                <div class="flex flex-column align-items-center p-3 sm:p-6">
                  <div class="bg-red-100 text-red-600 p-3 border-circle mb-3">
                    <i class="pi pi-times-circle text-2xl"></i>
                  </div>
                  <h3 class="text-lg sm:text-xl font-semibold text-gray-800 mb-2">Feed not found</h3>
                  <p class="text-gray-600 text-sm sm:text-base mb-3">This feed could not be loaded or doesn't exist</p>
                  <Button label="Back to My Feeds" icon="pi pi-arrow-left" @click="$router.push('/feeds')" />
                </div>
              </template>
            </Card>

            <!-- État 2: Chargement des models (parseFeedModels en cours) -->
            <main v-else-if="loadingModels"
                  class="text-center p-3 sm:p-6"
                  role="main"
                  aria-live="polite"
                  aria-busy="true"
                  aria-labelledby="loading-models-title">
              <div class="bg-blue-100 text-blue-500 p-3 sm:p-4 border-circle inline-flex mb-2 sm:mb-3 pi pi-spinner pi-spin">
              </div>
              <div id="loading-models-title" class="text-gray-600 font-semibold text-sm sm:text-base">Discovering data models...</div>
              <div class="text-gray-500 text-xs sm:text-sm mt-1">Analyzing your feed structure</div>
            </main>

            <!-- État 3: Chargement des fields/items (parseFeedModelStructureAndItems en cours) -->
            <main v-else-if="(loadingModelFields || loadingModelItems) && !showModelView"
                  class="text-center p-3 sm:p-6"
                  role="main"
                  aria-live="polite"
                  aria-busy="true"
                  aria-labelledby="loading-discovery-title">
              <div class="bg-blue-100 text-blue-500 p-3 sm:p-4 border-circle inline-flex mb-2 sm:mb-3 pi pi-spinner pi-spin">
              </div>
              <div id="loading-discovery-title" class="text-gray-600 font-semibold text-sm sm:text-base">Loading model data...</div>
              <div class="text-gray-500 text-xs sm:text-sm mt-1">This may take a moment depending on the size of your data</div>
            </main>

            <!-- État 4: Models découverts et données prêtes - Afficher la Card Discovery -->
            <Card v-else-if="showModelView && models.length > 0" class="discovery-card">
              <template #title>
                <div class="section-header">
                  <div class="section-icon discovery">
                    <i class="pi pi-compass"></i>
                  </div>
                  <div class="section-title-wrapper">
                    <h2 class="section-title">Data Discovery</h2>
                    <p class="section-subtitle">Explore and analyze your data structure</p>
                  </div>
                </div>
              </template>
              <template #content>
                <!-- Model details view -->
                <div data-model-view>
                  <div v-if="models.length > 0" class="model-selection-header mb-4">
                    <div
                        class="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 lg:p-4 border-round-lg border-1 border-blue-200">
                      <div class="w-full">
                        <div class="mb-3">
                          <h3 class="text-base lg:text-lg font-semibold text-gray-800 m-0 mb-1">Data Model</h3>
                          <p class="text-xs md:text-sm text-gray-600 mb-0 m-0">
                            <span class="md:hidden">Choose data structure</span>
                            <span class="hidden md:inline">Choose which data structure to analyze and explore</span>
                          </p>
                        </div>
                        <div class="w-full">
                          <Dropdown v-model="selectedModel"
                                    :options="models"
                                    optionLabel="name"
                                    placeholder="Select model..."
                                    class="model-selection-dropdown w-full"
                                    filter
                                    filterPlaceholder="Search models..."
                                    @change="parseFeedModelStructureAndItems">
                            <template #value="slotProps">
                              <div v-if="slotProps.value" class="flex align-items-center">
                                <div class="model-badge-small">
                                  <i class="pi pi-sitemap"></i>
                                </div>
                                <div class="model-info-compact">
                                          <span class="model-name-compact">{{
                                              formatModelName(slotProps.value.name)
                                            }}</span>
                                </div>
                              </div>
                              <span v-else class="placeholder-text">{{ slotProps.placeholder }}</span>
                            </template>
                            <template #option="slotProps">
                              <div class="flex align-items-center option-item">
                                <div class="option-badge">
                                  <i class="pi pi-sitemap"></i>
                                </div>
                                <div class="option-info">
                                  <span class="option-name">{{ formatModelName(slotProps.option.name) }}</span>
                                  <small class="option-path">{{ slotProps.option.name }}</small>
                                </div>
                              </div>
                            </template>
                          </Dropdown>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Loading state -->
                  <main v-if="loadingModelFields || loadingModelItems"
                        class="text-center p-3 sm:p-6"
                        role="main"
                        aria-live="polite"
                        aria-busy="true"
                        aria-labelledby="loading-tabs-title">
                    <div class="bg-blue-100 text-blue-500 p-3 sm:p-4 border-circle inline-flex mb-2 sm:mb-3 pi pi-spinner pi-spin">
                    </div>
                    <div id="loading-tabs-title" class="text-gray-600 font-semibold text-sm sm:text-base">Loading model data...</div>
                    <div class="text-gray-500 text-xs sm:text-sm mt-1">This may take a moment depending on the size of your data</div>
                  </main>

                  <TabView v-else class="w-full">
                    <!-- Fields Tab -->
                    <TabPanel>
                      <template #header>
                        <div class="flex align-items-center">
                          <i class="pi pi-list mr-2"></i>
                          <span>Fields</span>
                        </div>
                      </template>

                      <!-- Model Selection Header -->

                      <!-- Side-by-Side Fields Layout -->
                      <!-- Empty State -->
                      <div v-if="!modelFields || modelFields.length === 0" class="text-center p-6">
                        <div class="bg-gray-100 text-gray-400 p-4 border-circle mb-4 inline-block">
                          <i class="pi pi-list text-3xl"></i>
                        </div>
                        <h3 class="text-lg text-gray-600 mb-2">No fields available</h3>
                        <p class="text-gray-500 text-sm mb-4">Select a data model to view its field structure</p>
                        <div v-if="!selectedModel" class="text-center">
                          <i class="pi pi-arrow-up text-blue-500 mr-2"></i>
                          <span class="text-blue-600 text-sm">Choose a model above to get started</span>
                        </div>
                      </div>

                      <!-- Mobile View: Stack Layout -->
                      <div v-else-if="!displayedModelField" class="mobile-fields-layout md:hidden">
                        <!-- Header without Card - Hidden when viewing field values on mobile -->
                        <div class="flex justify-content-between align-items-center mb-3 md:hidden">
                          <div class="flex align-items-center gap-2">
                            <i class="pi pi-list text-blue-600"></i>
                            <h3 class="text-base font-semibold m-0">Field Names</h3>
                            <Chip
                                v-if="filteredModelFields && modelFields && filteredModelFields.length !== modelFields.length"
                                :label="`${filteredModelFields.length}/${modelFields.length}`"
                                class="bg-orange-100 text-orange-700 text-xs"/>
                            <Chip v-else-if="modelFields" :label="modelFields.length.toString()"
                                  class="bg-blue-100 text-blue-700 text-xs"/>
                          </div>
                        </div>
                        <!-- Content without Card -->
                        <!-- Search - Hidden when viewing field values on mobile -->
                        <div class="mb-3 md:block">
                          <InputGroup>
                            <Dropdown
                                v-model="fieldFilterOperator"
                                :options="fieldFilterOperatorOptions"
                                optionLabel="label"
                                optionValue="value"
                                class="w-auto"
                                style="max-width: 65px;"
                            >
                              <template #value="slotProps">
                                <i :class="fieldFilterOperatorOptions.find(o => o.value === slotProps.value)?.icon" v-tooltip.top="fieldFilterOperatorOptions.find(o => o.value === slotProps.value)?.label" />
                              </template>
                              <template #option="slotProps">
                                <div class="flex align-items-center gap-2">
                                  <i :class="slotProps.option.icon" />
                                  <span>{{ slotProps.option.label }}</span>
                                </div>
                              </template>
                            </Dropdown>
                            <InputText
                                v-model="modelFieldFilters['global'].value"
                                :placeholder="'Search fields (' + fieldFilterOperatorOptions.find(o => o.value === fieldFilterOperator)?.label.toLowerCase() + ')...'"
                                class="w-full"/>
                          </InputGroup>
                          <div class="flex align-items-center gap-1 mt-2 flex-wrap">
                            <div v-for="opt in completenessColorOptions" :key="opt.value"
                                 class="cursor-pointer border-circle flex align-items-center justify-content-center transition-all duration-200"
                                 :style="{
                                   width: '24px',
                                   height: '24px',
                                   backgroundColor: selectedCompletenessColors.includes(opt.value) ? opt.color : 'transparent',
                                   border: '2px solid ' + opt.color,
                                   opacity: selectedCompletenessColors.includes(opt.value) ? 1 : 0.5,
                                 }"
                                 v-tooltip.top="opt.label"
                                 @click="toggleCompletenessColor(opt.value)">
                              <i v-if="selectedCompletenessColors.includes(opt.value)" class="pi pi-check" style="font-size: 0.6rem; color: white;" />
                            </div>
                          </div>
                        </div>

                        <!-- Empty search results - Hidden when viewing field values on mobile -->
                        <div
                            v-if="filteredModelFields && filteredModelFields.length === 0 && modelFieldFilters.global.value"
                            class="text-center p-6 md:block">
                          <i class="pi pi-search text-3xl text-gray-300 mb-3"></i>
                          <h4 class="text-gray-600 mb-2">No fields found</h4>
                          <p class="text-gray-500 text-sm">Try adjusting your search terms</p>
                        </div>

                        <!-- Field Cards - Hidden when viewing field values on mobile -->
                        <div v-for="field in (filteredModelFields || [])" :key="field.id"
                             class="field-card mb-3 border-round-lg shadow-1 overflow-hidden">
                          <!-- Header - More compact without actions -->
                          <div class="field-card-header p-3 bg-gradient-to-r from-blue-50 to-indigo-50">
                            <div class="text-center w-full">
                              <div class="flex align-items-center justify-content-center gap-2 mb-1">
                                <i class="pi pi-tag text-blue-600 text-sm"></i>
                                <div class="font-semibold text-gray-800 truncate">{{ field.name }}</div>
                              </div>
                              <div class="flex gap-1 justify-content-center flex-wrap">
                                <template v-if="getFieldValueCount(field)">
                                  <Chip :label="`${getFieldValueCount(field)} unique values`"
                                        class="bg-blue-100 text-blue-700 text-xs"/>
                                  <Chip :label="`${getFieldTotalCount(field)} total`"
                                        class="bg-green-100 text-green-700 text-xs"/>
                                </template>
                                <template v-else>
                                  <Chip label="No data available"
                                        class="bg-orange-100 text-orange-700 text-xs"/>
                                </template>
                              </div>
                              <!-- Completeness indicator for mobile -->
                              <div v-if="getFieldCompleteness(field) !== null" class="mt-2">
                                <div class="flex align-items-center justify-content-center gap-2">
                                  <span class="text-xs text-gray-600">Completeness:</span>
                                  <div class="completeness-bar" style="width: 60px; height: 6px; background: #e5e7eb; border-radius: 3px; overflow: hidden;">
                                    <div :style="{
                                      width: getFieldCompleteness(field) + '%',
                                      height: '100%',
                                      backgroundColor: getCompletenessColor(getFieldCompleteness(field)) === 'green' ? '#22c55e' :
                                                       getCompletenessColor(getFieldCompleteness(field)) === 'blue' ? '#3b82f6' :
                                                       getCompletenessColor(getFieldCompleteness(field)) === 'yellow' ? '#eab308' :
                                                       getCompletenessColor(getFieldCompleteness(field)) === 'orange' ? '#f97316' : '#ef4444',
                                      transition: 'width 0.3s ease'
                                    }"></div>
                                  </div>
                                  <span class="text-xs font-medium"
                                        :class="{
                                          'text-green-600': getCompletenessColor(getFieldCompleteness(field)) === 'green',
                                          'text-blue-600': getCompletenessColor(getFieldCompleteness(field)) === 'blue',
                                          'text-yellow-600': getCompletenessColor(getFieldCompleteness(field)) === 'yellow',
                                          'text-orange-600': getCompletenessColor(getFieldCompleteness(field)) === 'orange',
                                          'text-red-600': getCompletenessColor(getFieldCompleteness(field)) === 'red'
                                        }">{{ getFieldCompleteness(field) }}%</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Body - Field Path -->
                          <div class="field-card-body p-3 bg-white">
                            <div class="mb-2 text-gray-600 text-sm">Field Path:</div>
                            <div class="p-2 bg-gray-50 border-round">
                              <code class="text-sm font-mono text-gray-800 break-all">{{ field.path }}</code>
                            </div>
                          </div>

                          <!-- Footer - Actions -->
                          <div
                              class="field-card-footer p-2 bg-gray-50 flex justify-content-center border-top-1 border-gray-200">
                            <Button
                                :icon="getFieldValueCount(field) ? 'pi pi-eye' : 'pi pi-eye-slash'"
                                v-tooltip="getFieldValueCount(field) ? 'View Values' : 'No data'"
                                size="small"
                                :severity="getFieldValueCount(field) ? 'info' : 'secondary'"
                                :disabled="!getFieldValueCount(field)"
                                @click="showModelFieldValues(field)"/>
                          </div>
                        </div>
                      </div>

                      <!-- Desktop Side-by-Side Layout -->
                      <div class="fields-layout hidden md:block">
                        <div class="flex gap-4 h-full">
                          <!-- Fields Panel (50%) -->
                          <div class="fields-panel" style="width: 50%;">
                            <Card class="h-full">
                              <template #header>
                                <div
                                    class="flex justify-content-between align-items-start p-3 border-bottom-1 border-gray-200">
                                  <div class="flex align-items-center gap-2">
                                    <i class="pi pi-list text-blue-600 align-self-start mt-2"></i>
                                    <div class="flex-1">
                                      <h3 class="text-lg font-semibold m-0 flex align-items-center gap-2">
                                        <span>Field Names</span>
                                        <Chip
                                            v-if="filteredModelFields && modelFields && filteredModelFields.length !== modelFields.length"
                                            :label="`${filteredModelFields.length}/${modelFields.length}`"
                                            class="bg-orange-100 text-orange-700"/>
                                        <Chip v-else-if="modelFields" :label="modelFields.length.toString()"
                                              class="bg-blue-100 text-blue-700"/>
                                      </h3>
                                      <p class="text-sm text-gray-600 m-1">Explore the selected model fields</p>
                                    </div>
                                  </div>
                                  <div class="flex gap-2">
                                    <Button
                                        type="button"
                                        icon="pi pi-filter-slash"
                                        size="small"
                                        severity="secondary"
                                        text
                                        @click="clearModelFieldFilter()"/>
                                  </div>
                                </div>
                              </template>
                              <template #content>
                                <!-- Search -->
                                <div class="mb-3">
                                  <InputGroup>
                                    <Dropdown
                                        v-model="fieldFilterOperator"
                                        :options="fieldFilterOperatorOptions"
                                        optionLabel="label"
                                        optionValue="value"
                                        class="w-auto"
                                        style="max-width: 65px;"
                                    >
                                      <template #value="slotProps">
                                        <i :class="fieldFilterOperatorOptions.find(o => o.value === slotProps.value)?.icon" v-tooltip.top="fieldFilterOperatorOptions.find(o => o.value === slotProps.value)?.label" />
                                      </template>
                                      <template #option="slotProps">
                                        <div class="flex align-items-center gap-2">
                                          <i :class="slotProps.option.icon" />
                                          <span>{{ slotProps.option.label }}</span>
                                        </div>
                                      </template>
                                    </Dropdown>
                                    <InputText
                                        v-model="modelFieldFilters['global'].value"
                                        :placeholder="'Search fields (' + fieldFilterOperatorOptions.find(o => o.value === fieldFilterOperator)?.label.toLowerCase() + ')...'"
                                        class="w-full"/>
                                  </InputGroup>
                                  <div class="flex align-items-center gap-2 mt-2">
                                    <span class="text-xs text-gray-500">Completeness:</span>
                                    <div class="flex align-items-center gap-1">
                                      <div v-for="opt in completenessColorOptions" :key="opt.value"
                                           class="cursor-pointer border-circle flex align-items-center justify-content-center transition-all duration-200"
                                           :style="{
                                             width: '24px',
                                             height: '24px',
                                             backgroundColor: selectedCompletenessColors.includes(opt.value) ? opt.color : 'transparent',
                                             border: '2px solid ' + opt.color,
                                             opacity: selectedCompletenessColors.includes(opt.value) ? 1 : 0.5,
                                           }"
                                           v-tooltip.top="opt.label"
                                           @click="toggleCompletenessColor(opt.value)">
                                        <i v-if="selectedCompletenessColors.includes(opt.value)" class="pi pi-check" style="font-size: 0.6rem; color: white;" />
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <!-- Fields List -->
                                <div class="fields-list" style="max-height: 400px; overflow-y: auto;">
                                  <!-- Empty search results -->
                                  <div
                                      v-if="filteredModelFields && filteredModelFields.length === 0 && modelFieldFilters.global.value"
                                      class="text-center p-6">
                                    <i class="pi pi-search text-3xl text-gray-300 mb-3"></i>
                                    <h4 class="text-gray-600 mb-2">No fields found</h4>
                                    <p class="text-gray-500 text-sm">Try adjusting your search terms</p>
                                  </div>

                                  <div v-for="field in (filteredModelFields || [])" :key="field.id"
                                       class="field-item p-3 border-round-lg mb-2 cursor-pointer transition-all duration-200 hover:bg-blue-50 border-1 border-transparent hover:border-blue-200"
                                       :class="{ 'bg-blue-100 border-blue-300': selectedFieldForValues?.id === field.id }"
                                       @click="selectFieldForValues(field)">
                                    <div class="flex align-items-center justify-content-between">
                                      <div class="flex align-items-center gap-3 flex-1">
                                        <div class="bg-blue-100 text-blue-600 p-2 border-circle pi pi-tag"></div>
                                        <div class="flex-1 min-w-0">
                                          <div class="flex align-items-center justify-content-between mb-1">
                                            <div class="font-semibold text-gray-800 truncate">{{ field.name }}</div>
                                            <!-- Completeness indicator -->
                                            <div v-if="getFieldCompleteness(field) !== null"
                                                 class="flex align-items-center gap-2 ml-2"
                                                 v-tooltip.top="`${getFieldCompleteness(field)}% of items have a value for this field`">
                                              <div class="completeness-bar" style="width: 40px; height: 6px; background: #e5e7eb; border-radius: 3px; overflow: hidden;">
                                                <div :style="{
                                                  width: getFieldCompleteness(field) + '%',
                                                  height: '100%',
                                                  backgroundColor: getCompletenessColor(getFieldCompleteness(field)) === 'green' ? '#22c55e' :
                                                                   getCompletenessColor(getFieldCompleteness(field)) === 'blue' ? '#3b82f6' :
                                                                   getCompletenessColor(getFieldCompleteness(field)) === 'yellow' ? '#eab308' :
                                                                   getCompletenessColor(getFieldCompleteness(field)) === 'orange' ? '#f97316' : '#ef4444',
                                                  transition: 'width 0.3s ease'
                                                }"></div>
                                              </div>
                                              <span class="text-xs font-medium"
                                                    :class="{
                                                      'text-green-600': getCompletenessColor(getFieldCompleteness(field)) === 'green',
                                                      'text-blue-600': getCompletenessColor(getFieldCompleteness(field)) === 'blue',
                                                      'text-yellow-600': getCompletenessColor(getFieldCompleteness(field)) === 'yellow',
                                                      'text-orange-600': getCompletenessColor(getFieldCompleteness(field)) === 'orange',
                                                      'text-red-600': getCompletenessColor(getFieldCompleteness(field)) === 'red'
                                                    }">{{ getFieldCompleteness(field) }}%</span>
                                            </div>
                                          </div>
                                          <code class="text-xs text-gray-600">{{ field.path }}</code>
                                        </div>
                                      </div>
                                      <Button
                                          icon="pi pi-arrow-right"
                                          size="small"
                                          severity="info"
                                          text
                                          class="field-select-btn"/>
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </Card>
                          </div>

                          <!-- Field Values Panel (50%) -->
                          <div class="values-panel" style="width: 50%;">
                            <Card class="h-full">
                              <template #header>
                                <div class="flex align-items-center gap-2 p-3 border-bottom-1 border-gray-200">
                                  <i class="pi pi-chart-bar text-purple-600 align-self-start mt-2"></i>
                                  <div class="flex-1">
                                    <h3 class="text-lg font-semibold m-0 flex align-items-center gap-2">
                                      <template v-if="!selectedFieldForValues">
                                        <span>Field Values</span>
                                        <Chip
                                            label="Select a field"
                                            class="bg-gray-100 text-gray-600"/>
                                      </template>
                                      <template v-else>
                                        <span class="text-purple-600">Field Values</span>
                                        <i class="pi pi-tag text-purple-600"></i>
                                        <span class="text-purple-600">{{ selectedFieldForValues.name }}</span>
                                        <Chip
                                            v-if="modelFieldValues && modelFieldValues.length > 0"
                                            :label="modelFieldValues.length.toString()"
                                            class="bg-purple-100 text-purple-700"/>
                                        <Chip
                                            v-else
                                            label="No values"
                                            class="bg-gray-100 text-gray-600"/>
                                      </template>
                                    </h3>
                                    <p class="text-sm text-gray-600 m-1">
                                      <template v-if="!selectedFieldForValues">
                                        Select a field to view its unique values and frequency statistics
                                      </template>
                                      <template v-else>
                                        Unique values and frequency statistics for this field
                                      </template>
                                    </p>
                                  </div>
                                  <div class="flex align-items-center gap-2">

                                    <!-- Copy/Download All Stats Buttons -->
                                    <div
                                        v-if="selectedFieldForValues && modelFieldValues && modelFieldValues.length > 0"
                                        class="flex gap-1">
                                      <SplitButton
                                          v-tooltip="'Copy all field stats'"
                                          menuButtonIcon="pi pi-copy"
                                          label="Copy"
                                          size="small"
                                          severity="secondary"
                                          outlined
                                          :model="fieldStatsCopyOptions"
                                          @click="copyAllFieldStats('json')"/>
                                      <SplitButton
                                          v-tooltip="'Download all field stats'"
                                          menuButtonIcon="pi pi-download"
                                          label="Download"
                                          size="small"
                                          severity="secondary"
                                          outlined
                                          :model="fieldStatsDownloadOptions"
                                          @click="downloadAllFieldStats('json')"/>
                                    </div>
                                  </div>
                                </div>
                              </template>
                              <template #content>
                                <!-- Empty State -->
                                <div v-if="!selectedFieldForValues" class="text-center p-6">
                                  <div class="bg-gray-100 text-gray-400 p-4 border-circle mb-4 inline-block">
                                    <i class="pi pi-chart-bar text-3xl"></i>
                                  </div>
                                  <h3 class="text-lg text-gray-600 mb-2">No field selected</h3>
                                  <p class="text-gray-500 text-sm mb-4">Click on a field from the left panel to view
                                    its unique values and frequency statistics</p>
                                  <div class="text-center">
                                    <i class="pi pi-arrow-left text-blue-500 mr-2"></i>
                                    <span class="text-blue-600 text-sm">Select a field to get started</span>
                                  </div>
                                </div>

                                <!-- Field Values Content -->
                                <div v-else>
                                  <!-- Field Statistics Summary -->
                                  <div v-if="selectedFieldForValues && modelFieldValues && modelFieldValues.length > 0"
                                       class="mb-3 p-2 bg-purple-50 border-round">
                                    <div class="flex align-items-center justify-content-between">
                                      <div class="flex align-items-center gap-2">
                                        <i class="pi pi-chart-line text-purple-600 text-sm"></i>
                                        <span class="font-semibold text-purple-700 text-sm">Stats</span>
                                      </div>
                                      <div class="flex gap-3">
                                        <div class="text-center">
                                          <div class="font-bold text-base text-purple-600">{{
                                              modelFieldValues.length
                                            }}
                                          </div>
                                          <div class="text-xs text-gray-600">Unique</div>
                                        </div>
                                        <div class="text-center">
                                          <div class="font-bold text-base text-blue-600">{{
                                              totalFieldValueCount
                                            }}
                                          </div>
                                          <div class="text-xs text-gray-600">Total</div>
                                        </div>
                                        <div class="text-center">
                                          <div class="font-bold text-base text-green-600">
                                            {{ mostFrequentValue?.count || 0 }} ({{ mostFrequentValuePercentage }}%)
                                          </div>
                                          <div
                                              class="text-xs text-gray-600 max-w-20 overflow-hidden whitespace-nowrap text-overflow-ellipsis"
                                              :title="mostFrequentValue?.value || 'N/A'">
                                            {{
                                              (mostFrequentValue?.value || 'N/A').toString().substring(0, 15)
                                            }}{{ (mostFrequentValue?.value || '').toString().length > 15 ? '...' : '' }}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <!-- Search for Values -->
                                  <div v-if="modelFieldValues && modelFieldValues.length > 0" class="mb-3">
                                    <div class="flex gap-2">
                                      <IconField iconPosition="left" class="flex-1">
                                        <InputIcon class="pi pi-search"/>
                                        <InputText v-model="fieldValuesSearchTerm"
                                                   placeholder="Search values..."
                                                   class="w-full"/>
                                      </IconField>
                                      <Button
                                          :icon="fieldValuesSortOrder === 'desc' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'"
                                          :label="fieldValuesSortOrder === 'desc' ? 'Most Frequent' : 'Least Frequent'"
                                          size="small"
                                          severity="secondary"
                                          outlined
                                          @click="toggleSortOrder"/>
                                    </div>
                                  </div>

                                  <!-- Values List -->
                                  <div v-if="filteredFieldValues && filteredFieldValues.length > 0"
                                       class="values-list"
                                       style="max-height: 320px; overflow-y: auto; padding: 0.5rem;">
                                    <div v-for="value in filteredFieldValues" :key="value.id"
                                         class="value-item flex align-items-center justify-content-between p-2 border-round mb-1 bg-gray-50">
                                      <div class="flex align-items-center gap-2 flex-1">
                                        <i class="pi pi-circle-fill text-purple-500" style="font-size: 0.5rem;"></i>
                                        <span class="font-mono text-sm" v-html="value.value || 'null'"></span>
                                      </div>
                                      <div class="flex align-items-center gap-3">
                                        <div class="text-center">
                                          <div class="font-bold text-base text-purple-600">{{ value.count }}</div>
                                          <div class="text-xs text-gray-600">Count</div>
                                        </div>
                                        <div class="text-center">
                                          <div class="font-bold text-base text-blue-600">
                                            {{ getValuePercentage(value.count) }}%
                                          </div>
                                          <div class="text-xs text-gray-600">Percent</div>
                                        </div>
                                        <Button
                                            v-tooltip="'Copy this value stat'"
                                            icon="pi pi-copy"
                                            size="small"
                                            severity="secondary"
                                            text
                                            @click="copyFieldValueStat(value)"/>
                                        <Button
                                            v-tooltip="'Download this value stat'"
                                            icon="pi pi-download"
                                            size="small"
                                            severity="secondary"
                                            text
                                            @click="downloadFieldValueStat(value, selectedFieldForValues?.name)"/>
                                      </div>
                                    </div>
                                  </div>

                                  <!-- Empty Search Results -->
                                  <div
                                      v-else-if="modelFieldValues && modelFieldValues.length > 0 && fieldValuesSearchTerm"
                                      class="text-center p-6">
                                    <div class="bg-gray-100 text-gray-400 p-4 border-circle mb-4 inline-block">
                                      <i class="pi pi-search text-3xl"></i>
                                    </div>
                                    <h3 class="text-lg text-gray-600 mb-2">No matching values</h3>
                                    <p class="text-gray-500 text-sm">Try adjusting your search terms</p>
                                  </div>

                                  <!-- Empty State for Field with No Values -->
                                  <div v-else class="text-center p-6">
                                    <div class="bg-gray-100 text-gray-400 p-4 border-circle mb-4 inline-block">
                                      <i class="pi pi-ban text-3xl"></i>
                                    </div>
                                    <h3 class="text-lg text-gray-600 mb-2">No values found</h3>
                                    <p class="text-gray-500 text-sm">This field doesn't contain any unique values in
                                      the current dataset</p>
                                  </div>
                                </div>
                              </template>
                            </Card>
                          </div>
                        </div>
                      </div>

                      <!-- Field values view - Mobile only -->
                      <div v-if="displayedModelField" class="mt-2 md:hidden field-values-mobile">
                        <div class="field-values-container">
                          <!-- Header -->
                          <div class="flex align-items-center gap-3 mb-2">
                            <!-- Back button for mobile -->
                            <Button icon="pi pi-arrow-left"
                                    size="small"
                                    severity="secondary"
                                    outlined
                                    @click="backToFieldsList"
                                    class="mr-2 md:hidden"/>
                            <div class="bg-purple-100 text-purple-600 p-2 border-circle">
                              <i class="pi pi-list-check"></i>
                            </div>
                            <div class="flex-1">
                              <h2 class="text-xl font-semibold text-gray-800 m-0">{{ displayedModelField }}</h2>
                              <p class="text-gray-600 text-sm m-0">Field values</p>
                            </div>
                          </div>

                          <!-- Search for Values -->
                          <div v-if="modelFieldValues && modelFieldValues.length > 0" class="mb-2">
                            <IconField iconPosition="left">
                              <InputIcon class="pi pi-search"/>
                              <InputText v-model="fieldValuesSearchTerm"
                                         placeholder="Search values..."
                                         class="w-full"/>
                            </IconField>
                          </div>

                          <!-- Values List -->
                          <div v-if="filteredFieldValues && filteredFieldValues.length > 0"
                               class="values-list-container">
                            <div v-for="value in filteredFieldValues" :key="value.id"
                                 class="value-item flex align-items-center justify-content-between p-1 border-round mb-1 bg-gray-50">
                              <div class="flex align-items-center gap-2 flex-1 min-w-0">
                                <i class="pi pi-circle-fill text-purple-500" style="font-size: 0.75rem;"></i>
                                <span
                                    class="font-mono text-sm truncate cursor-pointer hover:text-blue-600 md:cursor-default"
                                    v-html="value.value || 'null'"
                                    @click="showValueDetail(value.value, value)"></span>
                              </div>
                              <div class="flex align-items-center gap-2">
                                <Chip :label="value.count.toString()" class="bg-purple-100 text-purple-700"/>
                              </div>
                            </div>
                          </div>

                          <!-- Empty Search Results -->
                          <div
                              v-else-if="modelFieldValues && modelFieldValues.length > 0 && fieldValuesSearchTerm"
                              class="text-center p-6">
                            <div class="bg-gray-100 text-gray-400 p-4 border-circle mb-4 inline-block">
                              <i class="pi pi-search text-3xl"></i>
                            </div>
                            <h3 class="text-lg text-gray-600">No results found</h3>
                            <p class="text-gray-500">Try adjusting your search term</p>
                          </div>

                          <!-- No Values Available -->
                          <div v-else
                               class="text-center p-6">
                            <div class="bg-gray-100 text-gray-400 p-4 border-circle mb-4 inline-block">
                              <i class="pi pi-list-check text-3xl"></i>
                            </div>
                            <h3 class="text-lg text-gray-600">No field values found</h3>
                          </div>
                        </div>
                      </div>
                    </TabPanel>
                    <!-- Items Tab -->
                    <TabPanel>
                      <template #header>
                        <div class="flex align-items-center">
                          <i class="pi pi-table mr-2"></i>
                          <span>Data Items</span>
                        </div>
                      </template>

                      <!-- Mobile: Actions Only - Shown outside card -->
                      <div v-if="modelItems && modelItems.length > 0 && !selectedItemDetail" class="mb-4 md:hidden">
                        <div class="p-2">
                          <div class="flex align-items-center gap-2 mb-3">
                            <div class="bg-blue-100 text-blue-700 px-2 py-1 border-round text-xs font-medium">
                              {{ modelItems.length }} items
                            </div>
                          </div>
                          <div class="flex gap-2 justify-content-center">
                            <Button
                                icon="pi pi-download"
                                label="Download"
                                size="small"
                                severity="info"
                                outlined
                                class="flex-1"
                                @click="downloadItems('json')"/>
                            <Button
                                icon="pi pi-copy"
                                label="Copy"
                                size="small"
                                severity="secondary"
                                outlined
                                class="flex-1"
                                @click="copyItems('json')"/>
                          </div>
                        </div>
                      </div>

                      <div v-if="modelItems && modelItems.length > 0 && selectedItemDetail" class="mb-4 md:hidden">
                        <div class="p-2">
                          <div class="flex gap-2 justify-content-center">
                            <Button
                                icon="pi pi-download"
                                label="Download"
                                size="small"
                                severity="info"
                                outlined
                                class="flex-1"
                                @click="downloadRowItem(selectedItemDetail)"/>
                            <Button
                                icon="pi pi-copy"
                                label="Copy"
                                size="small"
                                severity="secondary"
                                outlined
                                class="flex-1"
                                @click="copyRowItem(selectedItemDetail)"/>
                          </div>
                        </div>
                      </div>

                      <!-- Desktop Header Section -->
                      <div v-if="modelItems && modelItems.length > 0" class="mb-4 hidden md:block">
                        <Card class="border-round-lg shadow-1">
                          <template #content>
                            <!-- Desktop: Side-by-side Layout -->
                            <div class="flex justify-content-between align-items-center p-2">
                              <div class="flex align-items-center gap-2">
                                <i class="pi pi-table text-blue-600 text-xl"></i>
                                <h3 class="text-xl font-semibold text-gray-800 m-0">Data Items</h3>
                                <div class="bg-blue-100 text-blue-700 px-3 py-1 border-round text-sm font-medium">
                                  {{ modelItems.length }} items
                                </div>
                              </div>
                              <div class="flex gap-2">
                                <Button
                                    icon="pi pi-download"
                                    label="Download"
                                    size="small"
                                    severity="info"
                                    outlined
                                    @click="downloadItems('json')"/>
                                <Button
                                    icon="pi pi-copy"
                                    label="Copy"
                                    size="small"
                                    severity="secondary"
                                    outlined
                                    @click="copyItems('json')"/>
                              </div>
                            </div>
                          </template>
                        </Card>
                      </div>

                      <!-- Data Table -->
                      <div v-if="modelItems && modelItems.length > 0" class="hidden md:block">
                        <Card class="border-round-lg shadow-1">
                          <template #content>
                            <!-- Desktop: DataTable -->
                            <DataTable v-if="modelFieldKeys"
                                       v-model:filters="modelItemsFieldFilters"
                                       :value="modelItems"
                                       :sortOrder="-1"
                                       paginator
                                       :rows="25"
                                       :rowsPerPageOptions="[10, 25, 50, 100]"
                                       dataKey="id"
                                       :filterDisplay="showColumnFilters ? 'row' : null"
                                       :loading="loadingModelItems"
                                       :globalFilterFields="modelFieldKeys"
                                       class="hidden md:block w-full p-datatable-lg"
                                       scrollable
                                       scrollHeight="flex"
                                       responsive-layout="scroll"
                                       @value-change="updateFilteredItemsSize">
                              <template #header>
                                <!-- Mobile Header: Simplified -->
                                <div class="datatable-header-mobile md:hidden">
                                  <div class="flex align-items-center gap-2 mb-2">
                                    <div class="bg-blue-100 text-blue-700 px-2 py-1 border-round text-xs font-medium">
                                      {{
                                        selectedModelFields ? selectedModelFields.length : 0
                                      }}/{{ modelFieldKeys ? modelFieldKeys.length : 0 }} cols
                                    </div>
                                    <Button
                                        icon="pi pi-plus"
                                        size="small"
                                        severity="info"
                                        outlined
                                        v-tooltip="'More Columns'"
                                        @click="showColumnsDialog = true"/>
                                    <Button
                                        :icon="showColumnFilters ? 'pi pi-filter-fill' : 'pi pi-filter'"
                                        size="small"
                                        :severity="showColumnFilters ? 'info' : 'secondary'"
                                        outlined
                                        v-tooltip="showColumnFilters ? 'Hide Filters' : 'Show Filters'"
                                        @click="showColumnFilters = !showColumnFilters"/>
                                  </div>
                                  <div class="w-full">
                                    <IconField iconPosition="left">
                                      <InputIcon><i class="pi pi-search"/></InputIcon>
                                      <InputText
                                          v-model="modelItemsFieldFilters['global'].value"
                                          placeholder="Search..."
                                          class="w-full border-round-lg"/>
                                    </IconField>
                                  </div>
                                </div>

                                <!-- Desktop Header: Full -->
                                <div
                                    class="datatable-header-desktop hidden md:flex justify-content-between align-items-center p-2">
                                  <div class="flex align-items-center gap-2">
                                    <div class="bg-blue-100 text-blue-700 px-3 py-1 border-round text-sm font-medium">
                                      {{
                                        selectedModelFields ? selectedModelFields.length : 0
                                      }}/{{ modelFieldKeys ? modelFieldKeys.length : 0 }} columns displayed
                                    </div>
                                    <Button
                                        icon="pi pi-plus"
                                        label="More Columns"
                                        size="small"
                                        severity="info"
                                        outlined
                                        @click="showColumnsDialog = true"/>
                                    <Button
                                        :icon="showColumnFilters ? 'pi pi-filter-fill' : 'pi pi-filter'"
                                        :label="showColumnFilters ? 'Hide Column Filters' : 'Show Column Filters'"
                                        size="small"
                                        :severity="showColumnFilters ? 'info' : 'secondary'"
                                        outlined
                                        @click="showColumnFilters = !showColumnFilters"/>
                                  </div>
                                  <div class="flex align-items-center">
                                    <IconField iconPosition="left">
                                      <InputIcon><i class="pi pi-search"/></InputIcon>
                                      <InputText
                                          v-model="modelItemsFieldFilters['global'].value"
                                          placeholder="Search data..."
                                          class="border-round-lg"
                                          style="width: 250px"/>
                                    </IconField>
                                  </div>
                                </div>
                              </template>
                              <template #empty>
                                <div class="text-center p-6">
                                  <div class="bg-gray-100 text-gray-400 p-4 border-circle inline-flex mb-3">
                                    <i class="pi pi-table text-3xl"></i>
                                  </div>
                                  <h3 class="text-lg text-gray-600 mb-2">No data items found</h3>
                                  <p class="text-gray-500 text-sm">Try adjusting your search filters or column
                                    selection</p>
                                </div>
                              </template>
                              <template #loading>
                                <main class="text-center p-3 sm:p-6"
                                      role="main"
                                      aria-live="polite"
                                      aria-busy="true"
                                      aria-labelledby="loading-items-title">
                                  <div class="bg-blue-100 text-blue-500 p-3 sm:p-4 border-circle inline-flex mb-2 sm:mb-3 pi pi-spinner pi-spin">
                                  </div>
                                  <div id="loading-items-title" class="text-gray-600 font-semibold text-sm sm:text-base">Loading data items...</div>
                                  <div class="text-gray-500 text-xs sm:text-sm mt-1">Please wait while we fetch your data</div>
                                </main>
                              </template>
                              <template v-if="modelItems && modelItems.length">
                                <!-- Actions Column -->
                                <Column :style="'width: 120px'" frozen alignFrozen="right">
                                  <template #header>
                                    <div class="flex align-items-center gap-2">
                                      <i class="pi pi-cog text-blue-600 text-sm"></i>
                                      <span>Actions</span>
                                    </div>
                                  </template>
                                  <template #body="{ data, index }">
                                    <div class="flex justify-content-center">
                                      <ButtonGroup>
                                        <Button
                                            size="small"
                                            severity="info"
                                            outlined
                                            @click="openRowDialog(data, index)"
                                            v-tooltip.top="'View Details'"
                                        >
                                          <i class="pi pi-eye"></i>
                                        </Button>
                                        <Button
                                            size="small"
                                            severity="success"
                                            outlined
                                            @click="downloadRowItem(data)"
                                            v-tooltip.top="'Download'"
                                        >
                                          <i class="pi pi-download"></i>
                                        </Button>
                                        <Button
                                            size="small"
                                            severity="secondary"
                                            outlined
                                            @click="copyRowItem(data)"
                                            v-tooltip.top="'Copy'"
                                        >
                                          <i class="pi pi-copy"></i>
                                        </Button>
                                      </ButtonGroup>
                                    </div>
                                  </template>
                                </Column>
                                <!-- Data Columns -->
                                <Column v-for="fieldName in selectedModelFields"
                                        :key="fieldName"
                                        :field="fieldName"
                                        sortable
                                        class="data-table-column">
                                  <template #header>
                                    <div class="flex align-items-center gap-2">
                                      <i :class="getFieldIcon(fieldName)" class="text-blue-600 text-sm"></i>
                                      <span v-tooltip="fieldName">{{ cleanFieldName(fieldName) }}</span>
                                    </div>
                                  </template>
                                  <template #body="{ data }">
                                    <div class="flex align-items-center group" style="gap: 0.25rem;">
                                      <!-- Cell Content -->
                                      <div class="flex-1 min-w-0">
                                        <!-- Null/undefined values -->
                                        <div v-if="data[fieldName] === null || data[fieldName] === undefined"
                                             class="text-gray-400 italic text-sm">
                                          null
                                        </div>
                                        <!-- Object values -->
                                        <div v-else-if="typeof data[fieldName] === 'object' && data[fieldName] !== null"
                                             class="bg-blue-50 px-2 py-1 border-round text-sm font-mono text-blue-800">
                                          {{ JSON.stringify(data[fieldName]) }}
                                        </div>
                                        <!-- Specialized displays by field type -->
                                        <div v-else>
                                          <!-- Reference/ID display -->
                                          <div v-if="getFieldType(fieldName) === 'reference'"
                                               class="bg-gray-100 px-2 py-1 border-round text-sm font-mono text-gray-700">
                                            {{ data[fieldName] }}
                                          </div>
                                          <!-- Title display -->
                                          <div v-else-if="getFieldType(fieldName) === 'title'"
                                               class="font-semibold text-gray-800">
                                            {{ data[fieldName] }}
                                          </div>
                                          <!-- Date display -->
                                          <div v-else-if="getFieldType(fieldName) === 'date'"
                                               class="flex align-items-center gap-1">
                                            <i class="pi pi-calendar text-blue-600 text-xs"></i>
                                            <span class="text-sm">{{
                                                !data[fieldName] || data[fieldName] === null || data[fieldName] === 'null' ? 'No date' : data[fieldName]
                                              }}</span>
                                          </div>
                                          <!-- URL display -->
                                          <div v-else-if="getFieldType(fieldName) === 'url'">
                                            <a :href="data[fieldName]" target="_blank"
                                               class="text-blue-600 hover:text-blue-800 text-sm flex align-items-center gap-1 no-underline">
                                              <i class="pi pi-external-link text-xs"></i>
                                              <span class="truncate" style="max-width: 150px;">{{
                                                  data[fieldName]
                                                }}</span>
                                            </a>
                                          </div>
                                          <!-- Email display -->
                                          <div v-else-if="getFieldType(fieldName) === 'email'">
                                            <a :href="`mailto:${data[fieldName]}`"
                                               class="text-blue-600 hover:text-blue-800 text-sm flex align-items-center gap-1 no-underline">
                                              <i class="pi pi-envelope text-xs"></i>
                                              <span>{{ data[fieldName] }}</span>
                                            </a>
                                          </div>
                                          <!-- Phone display -->
                                          <div v-else-if="getFieldType(fieldName) === 'phone'">
                                            <a :href="`tel:${data[fieldName]}`"
                                               class="text-green-600 hover:text-green-800 text-sm flex align-items-center gap-1 no-underline">
                                              <i class="pi pi-phone text-xs"></i>
                                              <span>{{ data[fieldName] }}</span>
                                            </a>
                                          </div>
                                          <!-- User/Profile display -->
                                          <div v-else-if="getFieldType(fieldName) === 'user'"
                                               class="flex align-items-center gap-2">
                                            <div
                                                class="bg-blue-100 text-blue-700 w-6 h-6 border-circle flex align-items-center justify-content-center text-xs font-semibold">
                                              {{ data[fieldName]?.charAt(0)?.toUpperCase() || '?' }}
                                            </div>
                                            <span class="text-sm">{{ data[fieldName] }}</span>
                                          </div>
                                          <!-- Category/Tag display -->
                                          <div v-else-if="getFieldType(fieldName) === 'category'">
                                            <Tag :value="data[fieldName]" severity="info" class="text-xs"/>
                                          </div>
                                          <!-- Status display -->
                                          <div v-else-if="getFieldType(fieldName) === 'status'">
                                            <Tag :value="data[fieldName]"
                                                 :severity="data[fieldName]?.toLowerCase().includes('activ') ? 'success' : 'secondary'"
                                                 class="text-xs"/>
                                          </div>
                                          <!-- Content display (truncated) -->
                                          <div v-else-if="getFieldType(fieldName) === 'content'"
                                               class="text-sm text-gray-700">
                                            <div v-if="data[fieldName]?.length > 100"
                                                 v-tooltip="cleanHtmlForTooltip(data[fieldName])"
                                                 class="truncate cursor-pointer">
                                              {{ data[fieldName].substring(0, 100) }}...
                                            </div>
                                            <div v-else v-html="data[fieldName]"></div>
                                          </div>
                                          <!-- Default display -->
                                          <div v-else v-html="data[fieldName]"></div>
                                        </div>
                                      </div>
                                      <!-- Copy button -->
                                      <Button
                                          icon="pi pi-copy"
                                          size="small"
                                          severity="secondary"
                                          text
                                          class="opacity-30 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                          v-tooltip="'Copy Value'"
                                          @click="copyCellValue(data[fieldName])"/>
                                    </div>
                                  </template>
                                  <template v-if="showColumnFilters" #filter="{ filterModel, filterCallback }">
                                    <InputText v-model="filterModel.value"
                                               type="text"
                                               @input="filterCallback()"
                                               class="p-column-filter"
                                               :placeholder="`Search ${cleanFieldName(fieldName)} ...`"/>
                                  </template>
                                </Column>
                              </template>
                              <template #footer>
                                <div class="p-2 text-center">
                                  <div
                                      class="bg-blue-100 text-blue-700 px-2 py-1 border-round text-sm font-medium inline-block">
                                    {{ filteredRecords }} of {{ modelItems?.length || 0 }} items
                                  </div>
                                </div>
                              </template>
                            </DataTable>

                            <!-- Mobile: Item List -->

                          </template>
                        </Card>
                      </div>

                      <!-- temp -->
                      <div v-if="modelFieldKeys && !selectedItemDetail" class="mobile-items-list md:hidden">
                        <!-- Search Bar Mobile -->
                        <div class="mb-3">
                          <IconField iconPosition="left">
                            <InputIcon><i class="pi pi-search"/></InputIcon>
                            <InputText
                                v-model="modelItemsFieldFilters['global'].value"
                                placeholder="Search data..."
                                class="w-full border-round-lg"/>
                          </IconField>
                        </div>

                        <!-- Items List -->
                        <div v-if="modelItems && modelItems.length > 0" class="mobile-items-container">
                          <div v-for="(item, index) in modelItems"
                               :key="item.id || index"
                               class="mobile-item-card"
                               @click="showItemDetail(item)">
                            <div class="mobile-item-content">
                              <div class="flex-1 min-w-0">
                                <!-- Primary field (first non-id field) -->
                                <div class="mobile-item-primary">
                                  {{
                                    item[selectedModelFields?.[0]] || Object.values(item)[0] || 'Item #' + (index + 1)
                                  }}
                                </div>
                                <!-- Secondary info (second field or count) -->
                                <div class="mobile-item-secondary">
                                  {{
                                    selectedModelFields?.length > 1 ? item[selectedModelFields[1]] : 'ID: ' + (item.id || index + 1)
                                  }}
                                </div>
                              </div>
                              <div class="mobile-item-actions">
                                <i class="pi pi-chevron-right text-gray-400"></i>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Empty State Mobile -->
                        <div v-else class="text-center py-6">
                          <div class="bg-gray-100 text-gray-400 p-4 border-circle inline-block mb-3">
                            <i class="pi pi-database text-2xl"></i>
                          </div>
                          <h3 class="text-lg font-semibold text-gray-800 mb-2">No data items found</h3>
                          <p class="text-gray-600">Try adjusting your search or filters</p>
                        </div>
                      </div>

                      <!-- Item detail view - Mobile only -->
                      <div v-if="selectedItemDetail" class="mt-2 md:hidden item-detail-mobile">

                        <div class="item-detail-container">
                          <!-- Header -->
                          <div class="flex align-items-center gap-3 mb-2">
                            <!-- Back button for mobile -->
                            <Button icon="pi pi-arrow-left"
                                    size="small"
                                    severity="secondary"
                                    outlined
                                    @click="selectedItemDetail = null"
                                    class="mr-2 md:hidden"/>
                            <div class="bg-green-100 text-green-600 p-2 border-circle">
                              <i class="pi pi-table"></i>
                            </div>
                            <div class="flex-1">
                              <h2 class="text-xl font-semibold text-gray-800 m-0">Item Details</h2>
                              <p class="text-gray-600 text-sm m-0">Complete item information</p>
                            </div>
                          </div>


                          <!-- Item Details Grid -->
                          <div v-if="selectedItemDetail" class="item-details-grid">
                            <div v-for="(value, key) in selectedItemDetail"
                                 :key="key"
                                 class="item-detail-field">
                              <div class="item-detail-field-header">
                                <i class="pi pi-circle-fill text-green-500 mr-2" style="font-size: 0.75rem;"></i>
                                <span class="font-semibold text-gray-700">{{ key }}</span>
                              </div>
                              <div class="item-detail-field-value">
                                <div v-if="value === null || value === undefined"
                                     class="text-gray-400 italic font-mono text-sm">null
                                </div>
                                <div v-else-if="typeof value === 'object'"
                                     class="font-mono text-sm bg-gray-50 p-2 border-round overflow-auto"
                                     style="max-height: 100px;">
                                  {{ JSON.stringify(value, null, 2) }}
                                </div>
                                <div v-else-if="typeof value === 'boolean'"
                                     class="font-mono text-sm"
                                     :class="value ? 'text-green-600' : 'text-red-600'">
                                  {{ value }}
                                </div>
                                <div v-else-if="typeof value === 'number'"
                                     class="font-mono text-sm text-blue-600">
                                  {{ value }}
                                </div>
                                <div v-else
                                     class="font-mono text-sm break-words word-wrap-break-word overflow-wrap-break-word">
                                  {{ value }}
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Empty State -->
                          <div v-else class="text-center p-6">
                            <div class="bg-gray-100 text-gray-400 p-4 border-circle mb-4 inline-block">
                              <i class="pi pi-exclamation-triangle text-2xl"></i>
                            </div>
                            <h3 class="text-lg font-semibold text-gray-800 mb-2">No item selected</h3>
                            <p class="text-gray-600">Click an item from the list to view its details</p>
                          </div>
                        </div>
                      </div>
                      <!-- temp -->

                    </TabPanel>
                  </TabView>

                </div>
              </template>
            </Card>

            <!-- État 5: Aucun model trouvé -->
            <Card v-else class="shadow-2 border-round-lg">
              <template #content>
                <div class="flex flex-column align-items-center p-3 sm:p-6">
                  <div class="bg-orange-100 text-orange-600 p-3 border-circle mb-3">
                    <i class="pi pi-inbox text-2xl"></i>
                  </div>
                  <h3 class="text-lg sm:text-xl font-semibold text-gray-800 mb-2">No data models found</h3>
                  <p class="text-gray-600 text-sm sm:text-base mb-3">This feed doesn't contain any discoverable data structures</p>
                </div>
              </template>
            </Card>

          </TabPanel>

          <!-- Settings Tab -->
          <TabPanel>
            <template #header>
              <div class="flex align-items-center">
                <i class="pi pi-cog mr-2"></i>
                <span>Settings</span>
              </div>
            </template>

            <Card class="settings-card">
              <template #title>
                <div class="section-header">
                  <div class="section-icon settings">
                    <i class="pi pi-cog"></i>
                  </div>
                  <div class="section-title-wrapper">
                    <h2 class="section-title">Data Source Settings</h2>
                    <p class="section-subtitle">Configure your data source connection</p>
                  </div>
                </div>
              </template>
              <template #content>
                <form @submit.prevent="updateSource" class="p-fluid">
                  <!-- Basic Info Section -->
                  <div class="field mb-4">
                    <div class="formgrid grid">
                      <div class="field col-12 md:col-6">
                        <label for="name" class="font-semibold mb-2 block">Name</label>
                        <InputText id="name" v-model="editedSource.name" class="text-base"
                                   :class="{'p-invalid': v$.name.$invalid && submitted}"/>
                        <small v-if="v$.name.$invalid && submitted" class="p-error">Name is required</small>
                      </div>

                      <div class="field col-12 md:col-6">
                        <label for="provider" class="font-semibold mb-2 block">Provider</label>
                        <div class="p-inputtext p-component p-disabled text-base">
                          {{ editedSource.connection.provider }}
                        </div>
                      </div>

                      <div class="field col-12">
                        <label for="description" class="font-semibold mb-2 block">Description</label>
                        <Textarea id="description" v-model="editedSource.description" class="text-base"
                                  placeholder="Describe your feed..."
                                  rows="3"/>
                      </div>
                    </div>
                  </div>

                  <!-- File Actions Section - Only show for File sources -->
                  <div v-if="editedSource.connection.provider === 'file'" class="field mb-4">
                    <div class="formgrid grid">
                      <div class="field col-12">
                        <label class="font-semibold mb-2 block">File Actions</label>
                        <div class="flex align-items-center gap-2">
                          <Button v-tooltip.top="'Download current file'"
                                  icon="pi pi-download"
                                  size="small"
                                  severity="info"
                                  outlined
                                  @click="downloadFile"
                                  :disabled="!editedSource.connection.url"/>
                          <Button v-tooltip.top="'Upload new file'"
                                  icon="pi pi-upload"
                                  size="small"
                                  severity="warning"
                                  outlined
                                  @click="$refs.fileInput.click()"/>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Hidden file input for file replacement -->
                  <input ref="fileInput"
                         type="file"
                         style="display: none;"
                         @change="handleFileReplace"
                         accept=".json,.xml,.csv,.txt,.xlsx,.xls"/>

                  <!-- Connection Section - Only show for REST API sources -->
                  <div v-if="editedSource.kind === 'rest_api' || editedSource.connection.provider === 'generic_rest'"
                       class="field mb-4">
                    <div class="formgrid grid">
                      <div class="field col-12 md:col-6">
                        <label for="protocol" class="font-semibold mb-2 block">Protocol</label>
                        <div class="p-inputtext p-component p-disabled text-base">
                          {{ detectedProtocol }}
                        </div>
                      </div>

                      <div class="field col-12 md:col-6">
                        <label for="url" class="font-semibold mb-2 block">URL</label>
                        <div class="p-inputgroup">
                          <InputText id="url" v-model="editedSource.connection.url" class="text-base"
                                     :class="{'p-invalid': v$.connection && v$.connection.url && v$.connection.url.$invalid && submitted}"/>
                          <Button v-tooltip.left="'Copy URL'" icon="pi pi-copy"
                                  severity="info" text
                                  @click="copyFieldValue(editedSource.connection && editedSource.connection.url)"/>
                        </div>
                        <small v-if="v$.connection && v$.connection.url && v$.connection.url.$invalid && submitted"
                               class="p-error">Valid URL is
                          required</small>
                      </div>
                    </div>
                  </div>

                  <!-- Authentication Section - Only show for REST API sources -->
                  <div v-if="editedSource.kind === 'rest_api' || editedSource.connection.provider === 'generic_rest'"
                       class="field mb-4">
                    <div class="formgrid grid">
                      <div class="field col-12 md:col-6">
                        <label for="authMethod" class="font-semibold mb-2 block">Auth Method</label>
                        <Dropdown id="authMethod" v-model="authMethod" :options="authMethods" optionLabel="name"
                                  optionValue="value" placeholder="Select Auth Method" class="text-base"
                                  @change="updateAuthMethod"/>
                      </div>

                      <!-- Dynamic Authentication Fields -->
                      <template v-if="authMethod === 'basic'">
                        <div class="field col-12 md:col-6">
                          <label for="username" class="font-semibold mb-2 block">Username</label>
                          <div class="p-inputgroup">
                            <InputText id="username" v-model="editedSource.connection.auth.username" class="text-base"/>
                            <Button v-tooltip.left="'Copy Username'" icon="pi pi-copy"
                                    severity="info" text
                                    @click="copyFieldValue(editedSource.connection.auth.username)"/>
                          </div>
                        </div>
                        <div class="field col-12 md:col-6">
                          <label for="password" class="font-semibold mb-2 block">Password</label>
                          <div class="flex">
                            <Password id="password" v-model="editedSource.connection.auth.password" toggleMask
                                      class="text-base flex-grow-1"/>
                            <Button v-tooltip.left="'Copy Password'" icon="pi pi-copy" class="ml-2"
                                    severity="info" text
                                    @click="copyFieldValue(editedSource.connection.auth.password)"/>
                          </div>
                        </div>
                      </template>

                      <template v-if="authMethod === 'oauth2'">
                        <div class="field col-12 md:col-6">
                          <label for="clientId" class="font-semibold mb-2 block">Client ID</label>
                          <div class="p-inputgroup">
                            <InputText id="clientId" v-model="editedSource.connection.auth.client_id"
                                       class="text-base"/>
                            <Button v-tooltip.left="'Copy Client ID'" icon="pi pi-copy"
                                    severity="info" text
                                    @click="copyFieldValue(editedSource.connection.auth.client_id)"/>
                          </div>
                        </div>
                        <div class="field col-12 md:col-6">
                          <label for="clientSecret" class="font-semibold mb-2 block">Client Secret</label>
                          <div class="flex">
                            <Password id="clientSecret" v-model="editedSource.connection.auth.client_secret" toggleMask
                                      class="text-base flex-grow-1"/>
                            <Button v-tooltip.left="'Copy Client Secret'" icon="pi pi-copy" class="ml-2"
                                    severity="info" text
                                    @click="copyFieldValue(editedSource.connection.auth.client_secret)"/>
                          </div>
                        </div>
                        <div class="field col-12 md:col-6">
                          <label for="tokenUrl" class="font-semibold mb-2 block">Token URL</label>
                          <div class="p-inputgroup">
                            <InputText id="tokenUrl" v-model="editedSource.connection.auth.token_url"
                                       class="text-base"/>
                            <Button v-tooltip.left="'Copy Token URL'" icon="pi pi-copy"
                                    severity="info" text
                                    @click="copyFieldValue(editedSource.connection.auth.token_url)"/>
                          </div>
                        </div>
                        <div class="field col-12 md:col-6">
                          <label for="scopes" class="font-semibold mb-2 block">Scopes</label>
                          <Chips id="scopes" v-model="editedSource.connection.auth.scopes" class="text-base"/>
                        </div>
                      </template>

                      <template v-if="authMethod === 'bearer'">
                        <div class="field col-12">
                          <label for="bearerToken" class="font-semibold mb-2 block">Bearer Token</label>
                          <div class="flex">
                            <Password id="bearerToken" v-model="editedSource.connection.auth.bearer_token" toggleMask
                                      class="text-base flex-grow-1"/>
                            <Button v-tooltip.left="'Copy Bearer Token'" icon="pi pi-copy" class="ml-2"
                                    severity="info" text
                                    @click="copyFieldValue(editedSource.connection.auth.bearer_token)"/>
                          </div>
                        </div>
                      </template>

                      <template v-if="authMethod === 'api_key'">
                        <div class="field col-12 md:col-6">
                          <label for="keyName" class="font-semibold mb-2 block">Key Name</label>
                          <div class="p-inputgroup">
                            <InputText id="keyName" v-model="editedSource.connection.auth.key_name" class="text-base"/>
                            <Button v-tooltip.left="'Copy Key Name'" icon="pi pi-copy"
                                    severity="info" text
                                    @click="copyFieldValue(editedSource.connection.auth.key_name)"/>
                          </div>
                        </div>
                        <div class="field col-12 md:col-6">
                          <label for="keyValue" class="font-semibold mb-2 block">Key Value</label>
                          <div class="flex">
                            <Password id="keyValue" v-model="editedSource.connection.auth.key_value" toggleMask
                                      class="text-base flex-grow-1"/>
                            <Button v-tooltip.left="'Copy Key Value'" icon="pi pi-copy" class="ml-2"
                                    severity="info" text
                                    @click="copyFieldValue(editedSource.connection.auth.key_value)"/>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>

                  <!-- Options Section - Only show for REST API sources -->
                  <div v-if="editedSource.kind === 'rest_api' || editedSource.connection.provider === 'generic_rest'"
                       class="field mb-4">
                    <div v-for="(value, key) in editedSource.connection.options" :key="key" class="formgrid grid mb-2">
                      <div class="field col-12 md:col-5">
                        <InputText v-model="optionKeys[key]" placeholder="Option Key" class="text-base"/>
                      </div>
                      <div class="field col-12 md:col-5">
                        <div class="p-inputgroup">
                          <InputText v-model="editedSource.connection.options[key]" placeholder="Option Value"
                                     class="text-base"/>
                          <Button v-tooltip="'Copy Value'" icon="pi pi-copy"
                                  severity="info" text
                                  @click="copyFieldValue(editedSource.connection.options[key])"/>
                        </div>
                      </div>
                      <div class="field col-12 md:col-2">
                        <Button icon="pi pi-trash" severity="danger" outlined
                                @click="removeOption(key)"/>
                      </div>
                    </div>
                    <div class="flex justify-content-end mt-3">
                      <Button type="button" icon="pi pi-plus" label="Add Option" outlined
                              class="add-option-btn"
                              @click="addOption"/>
                    </div>
                  </div>

                  <!-- Actions -->
                  <!-- Actions -->
                  <div class="settings-actions">
                    <!-- Test Connection Button -->
                    <div class="test-connection-container">
                      <Button
                          v-if="editedSource.connection.provider !== 'content' && editedSource.connection.provider !== 'file'"
                          type="button" label="Test Connection" icon="pi pi-link"
                          severity="info" outlined
                          class="test-connection-btn"
                          @click="testConnection" :loading="testing"/>
                    </div>

                    <!-- Cancel and Save Buttons -->
                    <div class="save-buttons-container">
                      <Button type="button" label="Cancel" icon="pi pi-times"
                              severity="secondary" outlined
                              class="cancel-btn"
                              @click="resetForm"/>
                      <Button type="submit"
                              label="Save Changes"
                              icon="pi pi-check"
                              severity="success"
                              class="save-btn"
                              :loading="saving"/>
                    </div>
                  </div>
                </form>
              </template>
            </Card>
          </TabPanel>

        </TabView>
      </div>
    </template>

  </BaseLayout>

  <Toast/>

  <!-- Confirmation Dialog for Delete -->
  <Dialog v-model:visible="deleteDialog" :style="{width: '450px'}" header="Confirm Delete" :modal="true">
    <div class="flex flex-column align-items-center p-4">
      <div class="bg-red-100 text-red-600 p-3 border-circle mb-3 pi pi-exclamation-triangle">
      </div>
      <h3 class="text-xl font-semibold text-gray-800 mb-2">Delete Confirmation</h3>
      <p class="text-gray-600 mb-0 text-center" v-if="source">
        Are you sure you want to delete <strong>{{ source.name }}</strong>?
      </p>
      <div class="bg-red-50 border-left-3 border-red-500 p-3 mt-3 w-full">
        <div class="flex align-items-center">
          <i class="pi pi-info-circle text-red-600 mr-2"></i>
          <span class="text-red-800 font-semibold">Warning: This action cannot be undone</span>
        </div>
        <p class="text-red-700 text-sm m-0 mt-2">
          Deleting this feed will permanently remove all its configuration and data.
          You will be redirected to the feeds listing page.
        </p>
      </div>
    </div>
    <template #footer>
      <Button
          label="Cancel"
          icon="pi pi-times"
          severity="secondary"
          outlined
          class="mr-2"
          @click="deleteDialog = false"/>
      <Button
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          @click="deleteFeed"
          :loading="deleting"/>
    </template>
  </Dialog>

  <!-- Value Detail Dialog for Mobile -->
  <Dialog v-model:visible="showValueDetailDialog"
          modal
          :dismissableMask="true"
          class="value-detail-dialog">
    <template #header>
      <div class="flex align-items-center gap-2">
        <i class="pi pi-tag text-blue-600"></i>
        <span class="font-semibold">{{ selectedFieldForValues?.name || 'Field' }} - Value Detail</span>
      </div>
    </template>
    <div class="value-detail-content">
      <div class="value-header">
        <i class="pi pi-info-circle text-blue-500 mr-2"></i>
        <span class="font-semibold text-gray-700">Full Value Content</span>
      </div>
      <div class="value-display" v-html="selectedValueDetail"></div>
      <div class="value-stats" v-if="selectedValueForActions">
        <div class="stats-header">
          <i class="pi pi-chart-bar text-green-500 mr-2"></i>
          <span class="font-semibold text-gray-700">Statistics</span>
        </div>
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-label">Frequency:</span>
            <span class="stat-value">{{ selectedValueForActions.count }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Percentage:</span>
            <span class="stat-value">{{ selectedValuePercentage }}%</span>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="dialog-actions dialog-actions-centered">
        <Button v-tooltip="'Copy Value'"
                size="small"
                icon="pi pi-copy"
                severity="secondary"
                outlined
                @click="copyToClipboard(selectedValueDetail); showValueDetailDialog = false"/>
        <Button v-tooltip="'Copy Stats'"
                size="small"
                icon="pi pi-chart-line"
                severity="secondary"
                outlined
                v-if="selectedValueForActions"
                @click="copyFieldValueStat(selectedValueForActions); showValueDetailDialog = false"/>
        <Button v-tooltip="'Download Stats'"
                size="small"
                icon="pi pi-download"
                severity="info"
                v-if="selectedValueForActions"
                @click="downloadFieldValueStat(selectedValueForActions, selectedFieldForValues?.name || 'field'); showValueDetailDialog = false"/>
      </div>
    </template>
  </Dialog>

  <!-- Rich Row Viewer Dialog -->
  <Dialog v-model:visible="showRowDialog"
          :header="`Row Details (${selectedRowIndex + 1} of ${modelItems?.length || 0})`"
          :modal="true"
          :style="{ width: '80vw', maxWidth: '1200px' }"
          :draggable="false"
          maximizable
          class="row-dialog">
    <template #header>
      <div class="flex align-items-center justify-content-between w-full">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-eye text-blue-600"></i>
          <span class="font-semibold">Row Details</span>
          <div class="bg-blue-100 text-blue-700 px-2 py-1 border-round text-sm">
            {{ selectedRowIndex + 1 }} of {{ modelItems?.length || 0 }}
          </div>
        </div>
        <div class="flex gap-2">
          <!-- Navigation -->
          <Button
              icon="pi pi-chevron-left"
              size="small"
              severity="secondary"
              outlined
              :disabled="selectedRowIndex <= 0"
              v-tooltip="'Previous Row'"
              @click="navigateRow(-1)"/>
          <Button
              icon="pi pi-chevron-right"
              size="small"
              severity="secondary"
              outlined
              :disabled="selectedRowIndex >= (modelItems?.length || 0) - 1"
              v-tooltip="'Next Row'"
              @click="navigateRow(1)"/>
          <!-- View Mode Toggle -->
          <Button
              :icon="showRowAsJson ? 'pi pi-table' : 'pi pi-code'"
              :label="showRowAsJson ? 'Fields View' : 'JSON View'"
              size="small"
              severity="info"
              outlined
              @click="showRowAsJson = !showRowAsJson"/>
          <!-- Actions -->
          <Button
              icon="pi pi-download"
              label="Download"
              size="small"
              severity="info"
              outlined
              @click="downloadRowItem(selectedRowData)"/>
          <Button
              icon="pi pi-copy"
              label="Copy"
              size="small"
              severity="secondary"
              outlined
              @click="copyRowItem(selectedRowData)"/>
        </div>
        <!-- Column Info and Controls -->
        <div class="flex align-items-center gap-2 mt-2">
          <div class="bg-blue-100 text-blue-700 px-2 py-1 border-round text-sm font-medium">
            {{ getRowFieldKeys.length }}/{{ Object.keys(selectedRowData || {}).length }} columns displayed
          </div>
          <Button
              v-if="getRowFieldKeys.length < Object.keys(selectedRowData || {}).length"
              icon="pi pi-eye"
              label="Show All Columns"
              size="small"
              severity="secondary"
              outlined
              @click="showAllColumnsInDetail = true"/>
          <Button
              v-if="showAllColumnsInDetail"
              icon="pi pi-eye-slash"
              label="Show Selected Only"
              size="small"
              severity="secondary"
              outlined
              @click="showAllColumnsInDetail = false"/>
        </div>
      </div>
    </template>

    <!-- Dialog Content -->
    <div v-if="selectedRowData" class="row-dialog-content">
      <!-- JSON View -->
      <div v-if="showRowAsJson" class="json-view">
        <pre class="json-display">{{ JSON.stringify(selectedRowData, null, 2) }}</pre>
      </div>

      <!-- Fields View -->
      <div v-else class="fields-view">
        <div class="grid">
          <div v-for="fieldKey in getRowFieldKeys" :key="fieldKey" class="col-12 lg:col-6 xl:col-4 mb-3">
            <Card class="field-card h-full">
              <template #header>
                <div class="flex align-items-center gap-2 p-3 bg-gray-50">
                  <i :class="getFieldIcon(fieldKey)" class="text-blue-600"></i>
                  <span class="font-semibold text-gray-800">{{ cleanFieldName(fieldKey) }}</span>
                  <Button
                      icon="pi pi-copy"
                      size="small"
                      severity="secondary"
                      text
                      class="ml-auto"
                      v-tooltip="'Copy Field Value'"
                      @click="copyCellValue(selectedRowData[fieldKey])"/>
                </div>
              </template>
              <template #content>
                <div class="field-value p-3">
                  <!-- Null/undefined values -->
                  <div v-if="selectedRowData[fieldKey] === null || selectedRowData[fieldKey] === undefined"
                       class="text-gray-400 italic">
                    null
                  </div>
                  <!-- Object values -->
                  <div v-else-if="typeof selectedRowData[fieldKey] === 'object'"
                       class="bg-blue-50 p-3 border-round">
                    <pre class="text-sm font-mono text-blue-800 m-0">{{
                        JSON.stringify(selectedRowData[fieldKey], null, 2)
                      }}</pre>
                  </div>
                  <!-- Specialized displays -->
                  <div v-else>
                    <!-- Reference/ID -->
                    <div v-if="getFieldType(fieldKey) === 'reference'"
                         class="bg-gray-100 px-3 py-2 border-round font-mono text-gray-700">
                      {{ selectedRowData[fieldKey] }}
                    </div>
                    <!-- Title -->
                    <div v-else-if="getFieldType(fieldKey) === 'title'"
                         class="font-semibold text-gray-800 text-lg">
                      {{ selectedRowData[fieldKey] }}
                    </div>
                    <!-- Date -->
                    <div v-else-if="getFieldType(fieldKey) === 'date'"
                         class="flex align-items-center gap-2">
                      <i class="pi pi-calendar text-blue-600"></i>
                      <span>{{
                          !selectedRowData[fieldKey] || selectedRowData[fieldKey] === null || selectedRowData[fieldKey] === 'null' ? 'No date' : selectedRowData[fieldKey]
                        }}</span>
                    </div>
                    <!-- URL -->
                    <div v-else-if="getFieldType(fieldKey) === 'url'">
                      <a :href="selectedRowData[fieldKey]" target="_blank"
                         class="text-blue-600 hover:text-blue-800 flex align-items-center gap-2 no-underline">
                        <i class="pi pi-external-link"></i>
                        <span class="break-all">{{ selectedRowData[fieldKey] }}</span>
                      </a>
                    </div>
                    <!-- Email -->
                    <div v-else-if="getFieldType(fieldKey) === 'email'">
                      <a :href="`mailto:${selectedRowData[fieldKey]}`"
                         class="text-blue-600 hover:text-blue-800 flex align-items-center gap-2 no-underline">
                        <i class="pi pi-envelope"></i>
                        <span>{{ selectedRowData[fieldKey] }}</span>
                      </a>
                    </div>
                    <!-- Phone -->
                    <div v-else-if="getFieldType(fieldKey) === 'phone'">
                      <a :href="`tel:${selectedRowData[fieldKey]}`"
                         class="text-green-600 hover:text-green-800 flex align-items-center gap-2 no-underline">
                        <i class="pi pi-phone"></i>
                        <span>{{ selectedRowData[fieldKey] }}</span>
                      </a>
                    </div>
                    <!-- User -->
                    <div v-else-if="getFieldType(fieldKey) === 'user'"
                         class="flex align-items-center gap-3">
                      <div
                          class="bg-blue-100 text-blue-700 w-10 h-10 border-circle flex align-items-center justify-content-center font-semibold">
                        {{ selectedRowData[fieldKey]?.charAt(0)?.toUpperCase() || '?' }}
                      </div>
                      <span class="font-medium">{{ selectedRowData[fieldKey] }}</span>
                    </div>
                    <!-- Category -->
                    <div v-else-if="getFieldType(fieldKey) === 'category'">
                      <Tag :value="selectedRowData[fieldKey]" severity="info"/>
                    </div>
                    <!-- Status -->
                    <div v-else-if="getFieldType(fieldKey) === 'status'">
                      <Tag :value="selectedRowData[fieldKey]"
                           :severity="selectedRowData[fieldKey]?.toLowerCase().includes('activ') ? 'success' : 'secondary'"/>
                    </div>
                    <!-- Content (no truncation) -->
                    <div v-else-if="getFieldType(fieldKey) === 'content'"
                         class="text-gray-700 line-height-3"
                         v-html="selectedRowData[fieldKey]">
                    </div>
                    <!-- Default -->
                    <div v-else class="break-all" v-html="selectedRowData[fieldKey]"></div>
                  </div>
                </div>
              </template>
            </Card>
          </div>
        </div>
      </div>
    </div>
  </Dialog>

  <!-- Column Selection Dialog -->
  <Dialog v-model:visible="showColumnsDialog"
          header="Select Columns to Display"
          :modal="true"
          :style="{ width: '500px' }"
          :draggable="false">
    <div class="column-selection-content">
      <!-- Search for columns -->
      <div class="mb-3">
        <InputGroup>
          <Dropdown
              v-model="columnFilterOperator"
              :options="fieldFilterOperatorOptions"
              optionLabel="label"
              optionValue="value"
              class="w-auto"
              style="max-width: 65px;"
          >
            <template #value="slotProps">
              <i :class="fieldFilterOperatorOptions.find(o => o.value === slotProps.value)?.icon" v-tooltip.top="fieldFilterOperatorOptions.find(o => o.value === slotProps.value)?.label" />
            </template>
            <template #option="slotProps">
              <div class="flex align-items-center gap-2">
                <i :class="slotProps.option.icon" />
                <span>{{ slotProps.option.label }}</span>
              </div>
            </template>
          </Dropdown>
          <InputText v-model="columnSearchTerm"
                     :placeholder="'Search columns (' + fieldFilterOperatorOptions.find(o => o.value === columnFilterOperator)?.label.toLowerCase() + ')...'"
                     class="w-full"/>
        </InputGroup>
      </div>

      <!-- Column selection list -->
      <div class="column-list" style="max-height: 400px; overflow-y: auto;">
        <div v-for="field in filteredAvailableColumns" :key="field.id"
             class="column-item p-3 border-round mb-2 cursor-pointer transition-all duration-200 hover:bg-blue-50 border-1"
             :class="{ 'bg-blue-100 border-blue-300': selectedModelFields?.includes(field.path), 'border-gray-200': !selectedModelFields?.includes(field.path) }"
             @click="toggleColumnSelection(field.path)">
          <div class="flex align-items-center justify-content-between">
            <div class="flex align-items-center gap-3">
              <div class="bg-blue-100 text-blue-600 p-2 border-circle">
                <i :class="getFieldIcon(field.path)" class="text-sm"></i>
              </div>
              <div>
                <div class="font-semibold text-gray-800">{{ field.name }}</div>
                <code class="text-xs text-gray-600">{{ field.path }}</code>
              </div>
            </div>
            <div class="flex align-items-center">
              <i v-if="selectedModelFields?.includes(field.path)"
                 class="pi pi-check-circle text-blue-600 text-lg"></i>
              <i v-else class="pi pi-circle text-gray-400 text-lg"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="filteredAvailableColumns.length === 0" class="text-center p-6">
        <div class="bg-gray-100 text-gray-400 p-4 border-circle mb-4 inline-block">
          <i class="pi pi-search text-3xl"></i>
        </div>
        <h3 class="text-lg text-gray-600 mb-2">No columns found</h3>
        <p class="text-gray-500 text-sm">Try adjusting your search terms</p>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-between align-items-center w-full">
        <div class="flex gap-2">
          <Button label="Select All"
                  size="small"
                  severity="secondary"
                  outlined
                  :disabled="filteredAvailableColumns.length === 0"
                  @click="selectAllColumns"/>
          <Button label="Clear All"
                  size="small"
                  severity="secondary"
                  outlined
                  :disabled="filteredAvailableColumns.length === 0"
                  @click="clearAllColumns"/>
        </div>
        <div class="flex gap-2">
          <Button label="Close"
                  icon="pi pi-times"
                  size="small"
                  severity="secondary"
                  outlined
                  @click="showColumnsDialog = false"/>
        </div>
      </div>
    </template>
  </Dialog>

</template>

<script setup>
import {computed, nextTick, onMounted, reactive, ref, watch} from 'vue';
import {FilterMatchMode} from 'primevue/api';
import {useRoute, useRouter} from 'vue-router';
import {useToast} from 'primevue/usetoast';
import {useVuelidate} from '@vuelidate/core';
import {required, url} from '@vuelidate/validators';

// PrimeVue Components
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import InputIcon from 'primevue/inputicon';
import IconField from 'primevue/iconfield';
import Dropdown from 'primevue/dropdown';
import InputGroup from 'primevue/inputgroup';
import Password from 'primevue/password';
import Chips from 'primevue/chips';
import Chip from 'primevue/chip';
import Button from 'primevue/button';
import SplitButton from 'primevue/splitbutton';
import Tag from 'primevue/tag'
import Card from 'primevue/card';
import ModernBreadcrumb from '@/components/ModernBreadcrumb.vue';
import StandardHeader from '@/components/StandardHeader.vue';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import ButtonGroup from 'primevue/buttongroup';
import Dialog from 'primevue/dialog';
import Toast from 'primevue/toast';


// Custom components and services
import BaseLayout from '@/components/BaseLayout.vue';
import {menuLinks} from '@/shared/sharedLinks.js';
import apiService from '@/services/apiService';
import copy from 'copy-to-clipboard';

// Route and router
const route = useRoute();
const router = useRouter();
const toast = useToast();
const sourceId = route.params.id;

// Page state variables
const sideMenuLinks = ref(menuLinks);

// Breadcrumb navigation
const breadcrumbItems = computed(() => [
  {
    icon: 'pi pi-home',
    label: 'My Feeds',
    route: '/feeds'
  },
  {
    icon: 'pi pi-cog',
    label: source.value?.name || 'Feed Details',
    route: null // Current page, no route
  }
]);

// Header actions for StandardHeader
const headerActions = computed(() => [
  {
    icon: 'pi pi-plus',
    label: 'New Feed',
    mobileLabel: 'New',
    severity: 'success',
    size: 'small',
    mobileSize: 'small',
    command: navigateToSetup,
    tooltip: 'Create a new feed'
  },
  {
    icon: 'pi pi-trash',
    label: 'Delete',
    mobileLabel: 'Delete',
    severity: 'danger',
    outlined: true,
    size: 'small',
    mobileSize: 'small',
    command: confirmDelete,
    tooltip: 'Delete this feed'
  }
]);

// Mobile-first responsive design - using CSS breakpoints only
const loading = ref(true);
const saving = ref(false);
const testing = ref(false);
const submitted = ref(false);
const source = ref(null);
const editedSource = reactive({
  name: '',
  description: '',
  kind: '',
  connection: {
    provider: '',
    url: '',
    auth: null,
    options: {}
  }
});

const optionKeys = ref({});
const authMethod = ref(null);

// Form options

const authMethods = [
  {name: 'None', value: null},
  {name: 'Basic Auth', value: 'basic'},
  {name: 'OAuth 2.0', value: 'oauth2'},
  {name: 'Bearer Token', value: 'bearer'},
  {name: 'API Key', value: 'api_key'}
];

// Validation rules
const rules = computed(() => {
  const baseRules = {
    name: {required}
  };

  // Only require URL validation for REST API sources
  if (editedSource.kind === 'rest_api' || editedSource.connection.provider === 'generic_rest') {
    baseRules.connection = {
      url: {required, url}
    };
  }

  return baseRules;
});

// Ajouter cette propriété calculée dans la partie script
const detectedProtocol = computed(() => {
  if (!editedSource.connection || !editedSource.connection.url) return '-';

  // Extraire le protocole de l'URL
  const match = editedSource.connection.url.match(/^([a-z]+):\/\//i);
  if (match && match[1]) {
    return match[1].toUpperCase();
  }

  return '-';
});

const v$ = useVuelidate(rules, editedSource);

// Discovery state variables
const loadingModels = ref(false);
const loadingModelFields = ref(false);
const loadingModelItems = ref(false);
const models = ref([]);
const selectedModel = ref(null);
const modelFields = ref([]);
const modelFieldKeys = ref([]);
const modelItems = ref([]);
const selectedModelFields = ref([]);
const modelFieldValues = ref([]);
const modelFieldValueStats = ref({});
const displayedModelField = ref('');
const showModelView = ref(false);
const showValueDetailDialog = ref(false);
const selectedValueDetail = ref('');
const selectedValueForActions = ref(null);

// Item Detail Panel State
const selectedItemDetail = ref(null);
const activeTabIndex = ref(0);
const filteredFields = ref(modelFields.value.length);
const filteredRecords = ref(modelItems.value.length);
const filteredItems = ref([]);

// Field Values Panel State
const selectedFieldForValues = ref(null);
const fieldValuesSearchTerm = ref('');
const fieldValuesSortOrder = ref('desc'); // 'desc' = most frequent first, 'asc' = least frequent first

// Display Options Panel State

// Delete dialog state
const deleteDialog = ref(false);
const deleting = ref(false);

// Column selection dialog state
const showColumnsDialog = ref(false);
const columnSearchTerm = ref('');
const columnFilterOperator = ref('contains');

// Computed property for filtered available columns
const filteredAvailableColumns = computed(() => {
  if (!modelFieldKeys.value) return [];

  const searchTerm = columnSearchTerm.value.toLowerCase();
  if (!searchTerm) {
    return modelFieldKeys.value.map(key => ({
      key: key,
      name: cleanFieldName(key),
      path: key
    }));
  }

  const operator = columnFilterOperator.value;

  return modelFieldKeys.value
      .filter(key => {
        const name = cleanFieldName(key).toLowerCase();
        const path = key.toLowerCase();

        switch (operator) {
          case 'contains':
            return name.includes(searchTerm) || path.includes(searchTerm);
          case 'notContains':
            return !name.includes(searchTerm) && !path.includes(searchTerm);
          case 'startsWith':
            return path.startsWith(searchTerm);
          case 'endsWith':
            return path.endsWith(searchTerm);
          default:
            return name.includes(searchTerm) || path.includes(searchTerm);
        }
      })
      .map(key => ({
        key: key,
        name: cleanFieldName(key),
        path: key
      }));
});

// Column selection functions
const toggleColumnSelection = (fieldKey) => {
  if (!selectedModelFields.value) selectedModelFields.value = [];

  const index = selectedModelFields.value.indexOf(fieldKey);
  if (index > -1) {
    selectedModelFields.value.splice(index, 1);
  } else {
    selectedModelFields.value.push(fieldKey);
  }
};

const selectAllColumns = () => {
  const filtered = filteredAvailableColumns.value.map(f => f.path);
  const current = new Set(selectedModelFields.value || []);
  filtered.forEach(p => current.add(p));
  selectedModelFields.value = [...current];
};

const clearAllColumns = () => {
  const filtered = new Set(filteredAvailableColumns.value.map(f => f.path));
  selectedModelFields.value = (selectedModelFields.value || []).filter(p => !filtered.has(p));
};

// Column selection function removed - changes are applied automatically through Vue reactivity


// Legacy function - kept for compatibility
// const updateFilteredFieldsSize = (event) => {
//   filteredFields.value = event.length;
// };

const updateFilteredItemsSize = (event) => {
  filteredRecords.value = event.length;
  filteredItems.value = event;
};

const fieldFilterOperator = ref('contains');

const fieldFilterOperatorOptions = [
  { label: 'Contains', value: 'contains', icon: 'pi pi-search' },
  { label: 'Not contains', value: 'notContains', icon: 'pi pi-times' },
  { label: 'Starts with', value: 'startsWith', icon: 'pi pi-arrow-right' },
  { label: 'Ends with', value: 'endsWith', icon: 'pi pi-arrow-left' },
];

const selectedCompletenessColors = ref([]);

const completenessColorOptions = [
  { label: '90-100%', value: 'green', color: '#22c55e' },
  { label: '70-89%', value: 'blue', color: '#3b82f6' },
  { label: '50-69%', value: 'yellow', color: '#eab308' },
  { label: '25-49%', value: 'orange', color: '#f97316' },
  { label: '0-24%', value: 'red', color: '#ef4444' },
];

const toggleCompletenessColor = (colorValue) => {
  const idx = selectedCompletenessColors.value.indexOf(colorValue);
  if (idx > -1) {
    selectedCompletenessColors.value.splice(idx, 1);
  } else {
    selectedCompletenessColors.value.push(colorValue);
  }
};

const modelFieldFilters = ref({
  global: {value: null, matchMode: FilterMatchMode.CONTAINS},
  name: {value: null, matchMode: FilterMatchMode.STARTS_WITH},
  path: {value: null, matchMode: FilterMatchMode.CONTAINS}
});

// Computed for filtered fields (pure computed - no side effects)
const filteredModelFields = computed(() => {
  if (!modelFields.value) return [];

  let result = modelFields.value;

  // Filter by completeness color
  if (selectedCompletenessColors.value.length > 0) {
    result = result.filter(field => {
      const pct = getFieldCompleteness(field);
      const color = getCompletenessColor(pct);
      return selectedCompletenessColors.value.includes(color);
    });
  }

  // Filter by search term
  const searchTerm = modelFieldFilters.value?.global?.value?.toLowerCase() || '';
  if (!searchTerm) return result;

  const operator = fieldFilterOperator.value;

  return result.filter(field => {
    const name = field.name.toLowerCase();
    const path = field.path.toLowerCase();

    switch (operator) {
      case 'contains':
        return name.includes(searchTerm) || path.includes(searchTerm);
      case 'notContains':
        return !name.includes(searchTerm) && !path.includes(searchTerm);
      case 'startsWith':
        return path.startsWith(searchTerm);
      case 'endsWith':
        return path.endsWith(searchTerm);
      default:
        return name.includes(searchTerm) || path.includes(searchTerm);
    }
  });
});

// Watcher to update filteredFields count
watch(filteredModelFields, (newFields) => {
  filteredFields.value = newFields.length;
}, {immediate: true});

// Function to get appropriate icon for field names
const getFieldIcon = (fieldName) => {
  const lowerFieldName = fieldName.toLowerCase();

  // Common field type mappings
  if (lowerFieldName.includes('reference') || lowerFieldName.includes('ref') || lowerFieldName.includes('id')) {
    return 'pi pi-hashtag';
  }
  if (lowerFieldName.includes('title') || lowerFieldName.includes('name') || lowerFieldName.includes('heading')) {
    return 'pi pi-bookmark';
  }
  if (lowerFieldName.includes('date') || lowerFieldName.includes('time') || lowerFieldName.includes('created') || lowerFieldName.includes('updated')) {
    return 'pi pi-calendar';
  }
  if (lowerFieldName.includes('post') || lowerFieldName.includes('message') || lowerFieldName.includes('content') || lowerFieldName.includes('description')) {
    return 'pi pi-comment';
  }
  if (lowerFieldName.includes('user') || lowerFieldName.includes('author') || lowerFieldName.includes('profile') || lowerFieldName.includes('profil')) {
    return 'pi pi-user';
  }
  if (lowerFieldName.includes('url') || lowerFieldName.includes('link') || lowerFieldName.includes('href')) {
    return 'pi pi-external-link';
  }
  if (lowerFieldName.includes('email') || lowerFieldName.includes('mail')) {
    return 'pi pi-envelope';
  }
  if (lowerFieldName.includes('phone') || lowerFieldName.includes('tel')) {
    return 'pi pi-phone';
  }
  if (lowerFieldName.includes('status') || lowerFieldName.includes('state')) {
    return 'pi pi-check-circle';
  }
  if (lowerFieldName.includes('category') || lowerFieldName.includes('type') || lowerFieldName.includes('tag')) {
    return 'pi pi-tag';
  }

  // Default icon for unknown fields
  return 'pi pi-info-circle';
};

// Function to get field type for specialized display
const getFieldType = (fieldName) => {
  const lowerFieldName = fieldName.toLowerCase();

  if (lowerFieldName.includes('reference') || lowerFieldName.includes('ref') || lowerFieldName.includes('id')) {
    return 'reference';
  }
  if (lowerFieldName.includes('title') || lowerFieldName.includes('name') || lowerFieldName.includes('heading')) {
    return 'title';
  }
  if (lowerFieldName.includes('date') || lowerFieldName.includes('time') || lowerFieldName.includes('created') || lowerFieldName.includes('updated')) {
    return 'date';
  }
  if (lowerFieldName.includes('post') || lowerFieldName.includes('message') || lowerFieldName.includes('content') || lowerFieldName.includes('description')) {
    return 'content';
  }
  if (lowerFieldName.includes('user') || lowerFieldName.includes('author') || lowerFieldName.includes('profile') || lowerFieldName.includes('profil')) {
    return 'user';
  }
  if (lowerFieldName.includes('url') || lowerFieldName.includes('link') || lowerFieldName.includes('href')) {
    return 'url';
  }
  if (lowerFieldName.includes('email') || lowerFieldName.includes('mail')) {
    return 'email';
  }
  if (lowerFieldName.includes('phone') || lowerFieldName.includes('tel')) {
    return 'phone';
  }
  if (lowerFieldName.includes('status') || lowerFieldName.includes('state')) {
    return 'status';
  }
  if (lowerFieldName.includes('category') || lowerFieldName.includes('type') || lowerFieldName.includes('tag')) {
    return 'category';
  }

  return 'default';
};

// Function to clean HTML for readable tooltip display
const cleanHtmlForTooltip = (htmlString) => {
  if (!htmlString) return '';
  return htmlString
      .replace(/<br\s*\/?>/gi, '\n')  // Convert <br> to line breaks
      .replace(/<p[^>]*>/gi, '\n')    // Convert <p> to line breaks
      .replace(/<\/p>/gi, '\n')       // Convert </p> to line breaks
      .replace(/<[^>]*>/g, '')        // Remove all other HTML tags
      .replace(/&nbsp;/gi, ' ')       // Convert &nbsp; to spaces
      .replace(/&amp;/gi, '&')        // Convert &amp; to &
      .replace(/&lt;/gi, '<')         // Convert &lt; to <
      .replace(/&gt;/gi, '>')         // Convert &gt; to >
      .replace(/\n\s*\n/g, '\n')      // Remove multiple consecutive line breaks
      .trim();
};

// Toggle for showing/hiding column filters
const showColumnFilters = ref(false);

// Rich Row Viewer Dialog
const showRowDialog = ref(false);
const selectedRowData = ref(null);
const selectedRowIndex = ref(-1);
const showRowAsJson = ref(false);
const showAllColumnsInDetail = ref(false);

const modelItemsFieldFilters = ref({
  global: {value: null, matchMode: FilterMatchMode.CONTAINS}
});
const modelFieldValueFilters = ref({
  global: {value: null, matchMode: FilterMatchMode.CONTAINS}
});

// Computed for filtered field values based on search term
const filteredFieldValues = computed(() => {
  if (!modelFieldValues.value) return [];

  let filtered = modelFieldValues.value;

  // Apply search filter
  const searchTerm = fieldValuesSearchTerm.value.toLowerCase();
  if (searchTerm) {
    filtered = filtered.filter(item => {
      const valueStr = (item.value || 'null').toString().toLowerCase();
      return valueStr.includes(searchTerm);
    });
  }

  // Apply sorting by count (frequency)
  return filtered.sort((a, b) => {
    const countA = a.count || 0;
    const countB = b.count || 0;
    return fieldValuesSortOrder.value === 'desc' ? countB - countA : countA - countB;
  });
});

// Computed property to calculate percentage for selected value
const selectedValuePercentage = computed(() => {
  if (!selectedValueForActions.value || !modelFieldValues.value || modelFieldValues.value.length === 0) {
    return 0;
  }

  const totalCount = modelFieldValues.value.reduce((sum, item) => sum + (item.count || 0), 0);
  if (totalCount === 0) return 0;

  const valueCount = selectedValueForActions.value.count || 0;
  return ((valueCount / totalCount) * 100).toFixed(1);
});

// Field statistics computed properties
const totalFieldValueCount = computed(() => {
  if (!modelFieldValues.value || modelFieldValues.value.length === 0) return 0;
  return modelFieldValues.value.reduce((sum, item) => sum + (item.count || 0), 0);
});

const mostFrequentValue = computed(() => {
  if (!modelFieldValues.value || modelFieldValues.value.length === 0) return null;
  return modelFieldValues.value.reduce((max, item) =>
          (item.count || 0) > (max?.count || 0) ? item : max,
      modelFieldValues.value[0]
  );
});

const mostFrequentValuePercentage = computed(() => {
  if (!mostFrequentValue.value || totalFieldValueCount.value === 0) return 0;
  return ((mostFrequentValue.value.count / totalFieldValueCount.value) * 100).toFixed(1);
});

// Function to calculate percentage for any value
const getValuePercentage = (valueCount) => {
  if (!totalFieldValueCount.value || totalFieldValueCount.value === 0) return 0;
  return ((valueCount / totalFieldValueCount.value) * 100).toFixed(1);
};

// Function to toggle sort order
const toggleSortOrder = () => {
  fieldValuesSortOrder.value = fieldValuesSortOrder.value === 'desc' ? 'asc' : 'desc';
};

// These functions are kept for backward compatibility but are no longer used
// since we replaced the pill buttons with the "More columns" dialog system


// Load the data source on component mount
onMounted(async () => {

  if (sourceId) {
    try {
      loading.value = true;
      const response = await apiService.getSource(sourceId);
      source.value = response.data_source;

      console.log("Source value:", response.data_source);
      initializeForm();

      // Check for tab query parameter to auto-select the correct tab
      if (route.query.tab === 'discovery') {
        // Wait for the source to load first
        await nextTick();
        // Discovery is the first tab (index 0)
        activeTabIndex.value = 0;

        // Wait another tick to ensure tab is loaded
        await nextTick();

        // Launch parsing automatically
        await parseFeedModels();
      } else if (route.query.tab === 'settings') {
        // Wait for the source to load first
        await nextTick();
        // Settings is the second tab (index 1)
        activeTabIndex.value = 1;
      } else {
        // Default to Discovery tab if no tab parameter
        await nextTick();
        activeTabIndex.value = 0;

        // Wait another tick to ensure tab is loaded
        await nextTick();

        // Launch parsing automatically for default Discovery tab
        await parseFeedModels();
      }
    } catch (error) {
      console.error('Error loading data source:', error);
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: `Failed to load data source: ${error.message}`,
        life: 5000
      });
    } finally {
      loading.value = false;
    }
  } else {
    loading.value = false;
  }
});


// Watch for tab changes to update URL and automatically discover models when the Discovery tab is selected
watch(activeTabIndex, async (newValue) => {
  // Update URL to preserve tab state on refresh
  const tabName = newValue === 0 ? 'discovery' : newValue === 1 ? 'settings' : '';
  if (tabName && route.query.tab !== tabName) {
    await router.replace({
      path: route.path,
      query: {...route.query, tab: tabName}
    });
  }

  // If the Discovery tab is selected (index 0) and we have a source
  if (newValue === 0 && source.value) {
    // Only parse models if we haven't already done so
    if (!models.value.length && !loadingModels.value) {
      await parseFeedModels();
    }
  }
});


const selectPreferredModel = (modelsList) => {
  if (!modelsList || modelsList.length === 0) return null;

  const root = modelsList[0]

  // 1. Si on a $[*], c'est un array à la racine
  if (modelsList.includes('$[*]')) {
    return root;
  }

  // 2. Chercher le premier niveau de liste (pattern avec [*])
  // On cherche tous les chemins qui se terminent par [*]
  const arrayPaths = modelsList.filter(path => path.endsWith('[*]'));

  if (arrayPaths.length > 0) {
    // Trouver le chemin [*] le plus court (le plus proche de la racine)
    return arrayPaths.reduce((shortest, current) => {
      // Compter le nombre de niveaux (nombre de points)
      const shortestLevels = (shortest.match(/\./g) || []).length;
      const currentLevels = (current.match(/\./g) || []).length;

      return currentLevels < shortestLevels ? current : shortest;
    });
  }

  // 3. Chercher un objet unique ($.xxx avec des sous-propriétés)
  const rootLevelObjects = modelsList.filter(path => path.match(/^\$\.[^.[]+$/));


  if (rootLevelObjects.length > 0) {
    // Si on a plusieurs objets à la racine (comme $.offer ET $.company), c'est la racine
    if (rootLevelObjects.length > 1) {
      return root;
    }

    // Si on a un seul objet avec des sous-propriétés, c'est cet objet
    const singleObject = rootLevelObjects[0];
    const hasSubProperties = modelsList.some(path => path.startsWith(singleObject + '.'));

    if (hasSubProperties) {
      return singleObject;
    }
  }

  // 4. Par défaut, racine
  return root;
}


// Initialize form with source data
const initializeForm = () => {
  if (!source.value) return;

  editedSource.name = source.value.name;
  editedSource.description = source.value.description || '';
  editedSource.kind = source.value.kind;
  editedSource.connection.provider = source.value.connection.provider;
  editedSource.connection.url = source.value.connection.url;
  editedSource.connection.options = source.value.connection.options || {};

  // Initialize option keys
  optionKeys.value = {};
  Object.keys(editedSource.connection.options).forEach(key => {
    optionKeys.value[key] = key;
  });

  // Set auth method
  if (source.value.connection.auth) {
    authMethod.value = source.value.connection.auth.method;
    editedSource.connection.auth = {...source.value.connection.auth};
  } else {
    authMethod.value = null;
    editedSource.connection.auth = null;
  }
};

// Reset the form to original values
const resetForm = () => {
  initializeForm();
  submitted.value = false;
  v$.value.$reset();
};

// Update authentication method
const updateAuthMethod = () => {
  if (!authMethod.value) {
    editedSource.connection.auth = null;
    return;
  }

  // Initialize auth object based on method
  editedSource.connection.auth = {method: authMethod.value};

  switch (authMethod.value) {
    case 'basic':
      editedSource.connection.auth.username = '';
      editedSource.connection.auth.password = '';
      break;
    case 'oauth2':
      editedSource.connection.auth.client_id = '';
      editedSource.connection.auth.client_secret = '';
      editedSource.connection.auth.token_url = '';
      editedSource.connection.auth.scopes = [];
      break;
    case 'bearer':
      editedSource.connection.auth.bearer_token = '';
      break;
    case 'api_key':
      editedSource.connection.auth.key_name = '';
      editedSource.connection.auth.key_value = '';
      break;
  }
};

// Add a new option
const addOption = () => {
  const newKey = `option_${Object.keys(editedSource.connection.options).length + 1}`;
  editedSource.connection.options[newKey] = '';
  optionKeys.value[newKey] = newKey;
};

// Remove an option
const removeOption = (key) => {
  delete editedSource.connection.options[key];
  delete optionKeys.value[key];
  editedSource.connection.options = {...editedSource.connection.options};
};

// Update data source
const updateSource = async () => {
  submitted.value = true;
  const valid = await v$.value.$validate();

  if (!valid) return;

  try {
    saving.value = true;

    // Process options: rename keys based on optionKeys
    const processedOptions = {};
    Object.entries(editedSource.connection.options).forEach(([key, value]) => {
      processedOptions[optionKeys.value[key]] = value;
    });
    editedSource.connection.options = processedOptions;

    const response = await apiService.updateSource(sourceId, editedSource);
    source.value = response.data_source;

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Data source updated successfully',
      life: 3000
    });

    // Reset the form with new values
    initializeForm();
  } catch (error) {
    console.error('Error updating data source:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to update data source: ${error.message}`,
      life: 5000
    });
  } finally {
    saving.value = false;
    submitted.value = false;
  }
};

// Test connection
const testConnection = async () => {
  if (!sourceId) return;

  try {
    testing.value = true;
    const response = await apiService.testSourceConnection(sourceId);

    if (response.connection_test.is_alive) {
      toast.add({
        severity: 'success',
        summary: 'Connection Successful',
        detail: response.connection_test.message || 'Connection is working properly',
        life: 3000
      });
    } else {
      toast.add({
        severity: 'warn',
        summary: 'Connection Failed',
        detail: response.connection_test.message || 'Connection test failed',
        life: 5000
      });
    }
  } catch (error) {
    console.error('Error testing connection:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to test connection: ${error.message}`,
      life: 5000
    });
  } finally {
    testing.value = false;
  }
};

// Fonction pour adapter l'authentification au format attendu par l'API legacy
const getAuthModelFromSource = () => {
  if (!source.value || !source.value.connection) {
    return null;
  }

  // Si aucune authentification n'est présente
  if (!source.value.connection.auth) {
    return null;
  }

  // Mapper les informations d'authentification au format attendu par l'API legacy
  const auth = source.value.connection.auth;
  const authModel = {
    type: auth.method,
    body: {}
  };

  // Mapper les propriétés en fonction du type d'authentification
  switch (auth.method) {
    case 'basic':
      authModel.body = {
        username: auth.username,
        password: auth.password
      };
      break;
    case 'oauth2':
      authModel.body = {
        client_id: auth.client_id,
        client_secret: auth.client_secret,
        token_url: auth.token_url
      };

      // Gérer les scopes s'ils existent
      if (auth.scopes && auth.scopes.length > 0) {
        authModel.body.scopes = auth.scopes.join(',');
      }
      break;
    case 'bearer':
      // L'API legacy utilise api_key pour ce type
      authModel.type = 'api_key';
      authModel.body = {
        key_name: 'Authorization',
        key_value: `Bearer ${auth.bearer_token}`
      };
      break;
    case 'api_key':
      authModel.body = {
        key_name: auth.key_name,
        key_value: auth.key_value
      };
      break;
  }

  return authModel;
};


// Fonction modifiée pour le parseFeedModels
const parseFeedModels = async () => {
  loadingModels.value = true;
  resetDiscoveryViews();

  try {
    // Obtenir le modèle d'authentification adaptée à l'API legacy
    const authModel = getAuthModelFromSource();
    console.log("Auth:", authModel);

    const response = await apiService.parseFeedModelsPaths(
        authModel,
        'url',
        source.value.connection.url
    );

    // Vérifier si des modèles ont été trouvés
    if (response.results.parsed.models && response.results.parsed.models.length > 0) {
      models.value = response.results.parsed.models.map(item => ({name: item}));

      // Sélection automatique du modèle préféré
      const preferredModel = selectPreferredModel(response.results.parsed.models);
      if (preferredModel) {
        selectedModel.value = {name: preferredModel};
        console.log(`Modèle automatiquement sélectionné: ${preferredModel}`);

        // Lancer automatiquement le parsing du modèle sélectionné
        await parseFeedModelStructureAndItems();
      }


    } else {
      // Aucun modèle trouvé
      toast.add({
        severity: 'warn',
        summary: 'No Models Found',
        detail: 'No data models were found in this feed',
        life: 5000
      });
    }
  } catch (error) {
    console.error('Error parsing feed models:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to parse feed models: ${error.message}`,
      life: 5000
    });
  } finally {
    loadingModels.value = false;
  }
};

// Mise à jour de parseFeedModelStructureAndItems pour gérer les erreurs
const parseFeedModelStructureAndItems = async () => {
  if (!selectedModel.value) return;

  clearModelFieldFilter();
  loadingModelItems.value = true;
  loadingModelFields.value = true;
  showModelView.value = true;

  try {
    // Obtenir le modèle d'authentification adaptée à l'API legacy
    const authModel = getAuthModelFromSource();

    // Get model structure - Notez que l'API utilise model_path
    const structureResponse = await apiService.parseFeedModelStructure(
        authModel,
        selectedModel.value.name,
        'url',
        source.value.connection.url
    );

    modelFields.value = structureResponse.results.parsed.model.fields.map((item, index) => ({
      id: index,
      name: item.name,
      path: item.absolute_json_path
    }));

    modelFieldValueStats.value = structureResponse.results.parsed.model.stats;

    // Get model items - Notez que l'API utilise model_path
    const itemsResponse = await apiService.parseFeedModelItems(
        authModel,
        selectedModel.value.name,
        'url',
        source.value.connection.url
    );

    modelItems.value = itemsResponse.results.parsed.items;
    modelFieldKeys.value = itemsResponse.results.parsed.model_keys;
    initModelItemsFieldFilters();
    await nextTick();

  } catch (error) {
    console.error('Error parsing feed model:', error);
    showModelView.value = false;
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to parse feed model: ${error.message}`,
      life: 5000
    });

  } finally {
    loadingModelFields.value = false;
    loadingModelItems.value = false;
  }
};

// New function for side-by-side field selection
const selectFieldForValues = (field) => {
  selectedFieldForValues.value = field;
  fieldValuesSearchTerm.value = ''; // Reset search when selecting a new field

  if (modelFieldValueStats.value && modelFieldValueStats.value[field.path]) {
    const fvals = modelFieldValueStats.value[field.path];
    const fvals2 = Object.entries(fvals).map(([key, value]) => ({
      id: `${field.path}_${key}`, // Add unique ID
      value: key,
      count: value
    }));

    modelFieldValues.value = fvals2;
  } else {
    modelFieldValues.value = [];
  }
};

// Simple functions to get field statistics from existing data
const getFieldValueCount = (field) => {
  if (!modelFieldValueStats.value || !modelFieldValueStats.value[field.path]) {
    return 0;
  }
  return Object.keys(modelFieldValueStats.value[field.path]).length;
};

const getFieldTotalCount = (field) => {
  if (!modelFieldValueStats.value || !modelFieldValueStats.value[field.path]) {
    return 0;
  }
  const fieldValues = modelFieldValueStats.value[field.path];
  return Object.values(fieldValues).reduce((total, count) => total + count, 0);
};

// Calculate field completeness percentage (non-null values / total items)
const getFieldCompleteness = (field) => {
  if (!modelItems.value || modelItems.value.length === 0) {
    return null;
  }

  const totalItems = modelItems.value.length;
  const fieldTotal = getFieldTotalCount(field);

  if (fieldTotal === 0) {
    return 0;
  }

  // Check if there are null/empty values in the stats
  const fieldStats = modelFieldValueStats.value?.[field.path];
  if (!fieldStats) {
    return 0;
  }

  // Count non-null/non-empty values
  let nonNullCount = 0;
  for (const [value, count] of Object.entries(fieldStats)) {
    // Exclude null, empty string, 'null' string, and undefined
    if (value !== '' && value !== 'null' && value !== null && value !== 'undefined') {
      nonNullCount += count;
    }
  }

  return Math.round((nonNullCount / totalItems) * 100);
};

// Get completeness color based on percentage
const getCompletenessColor = (percentage) => {
  if (percentage === null) return 'gray';
  if (percentage >= 90) return 'green';
  if (percentage >= 70) return 'blue';
  if (percentage >= 50) return 'yellow';
  if (percentage >= 25) return 'orange';
  return 'red';
};

const showModelFieldValues = (field) => {
  clearModelFieldValuesFilter();

  if (modelFieldValueStats.value && modelFieldValueStats.value[field.path]) {
    const fvals = modelFieldValueStats.value[field.path];
    const fvals2 = Object.entries(fvals).map(([key, value]) => ({
      value: key,
      count: value
    }));

    modelFieldValues.value = fvals2;
    displayedModelField.value = field.name;

    nextTick(() => {
      scrollToElementPosition('modelFieldValues');
    });
  }
};

// Legacy function - kept for compatibility but unused in new layout
// const copyModelFieldValues = (field) => {
//   if (field && modelFieldValueStats.value && modelFieldValueStats.value[field.path]) {
//     // Copy the values of the specified field
//     const values = Object.keys(modelFieldValueStats.value[field.path]);
//     const dataToCopy = JSON.stringify(values, null, 2);
//     copy(dataToCopy);

//     toast.add({
//       severity: 'info',
//       summary: 'Success',
//       detail: 'Values copied',
//       life: 3000
//     });
//   } else {
//     console.log("Nothing to copy");
//   }
// };

// Function to copy a field value to clipboard
const copyFieldValue = (value) => {
  if (value) {
    copy(value);
    toast.add({
      severity: 'info',
      summary: 'Success',
      detail: 'Value copied to clipboard',
      life: 3000
    });
  } else {
    console.log("Nothing to copy");
  }
};

const scrollToElementPosition = async (refName) => {
  await nextTick();
  const el = document.getElementById(refName);
  if (el) {
    window.scrollTo({
      top: el.offsetTop,
      behavior: 'smooth'
    });
  }
};

const clearModelFieldFilter = () => {
  initModelFieldFilters();
};

const clearModelFieldValuesFilter = () => {
  initModelFieldValueFilters();
};


const initModelFieldFilters = () => {
  fieldFilterOperator.value = 'contains';
  selectedCompletenessColors.value = [];
  modelFieldFilters.value = {
    global: {value: null, matchMode: FilterMatchMode.CONTAINS},
    name: {value: null, matchMode: FilterMatchMode.STARTS_WITH},
    path: {value: null, matchMode: FilterMatchMode.CONTAINS}
  };
};

const initModelFieldValueFilters = () => {
  modelFieldValueFilters.value = {
    global: {value: null, matchMode: FilterMatchMode.CONTAINS}
  };
};

const initModelItemsFieldFilters = () => {
  if (!modelFieldKeys.value) return;

  const filters = {
    global: {value: null, matchMode: FilterMatchMode.CONTAINS}
  };

  modelFieldKeys.value.forEach(key => {
    filters[key] = {value: null, matchMode: FilterMatchMode.CONTAINS};
  });

  selectedModelFields.value = modelFieldKeys.value.slice(0, 5);
  modelItemsFieldFilters.value = filters;
};

const cleanFieldName = (fieldPath) => {
  if (!fieldPath) return '';

  // Remove the root path (e.g., "root.items[0].title" becomes "title")
  const parts = fieldPath.split('.');
  const lastPart = parts[parts.length - 1];

  // Remove brackets and indices (e.g., "items[0]" becomes "items")
  return lastPart.replace(/\[\d+\]/g, '');
};

const formatModelName = (modelName) => {
  if (!modelName) return '';

  // Replace dots and brackets with readable separators
  return modelName
      .replace(/\$/g, 'Root')
      .replace(/\[(\*|\d+)\]/g, '[]')
      .replace(/\./g, ' › ')
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capital letters
      .trim();
};

// Copy cell value to clipboard
const copyCellValue = async (value) => {
  try {
    const textValue = value === null || value === undefined ? 'null' :
        typeof value === 'object' ? JSON.stringify(value) :
            String(value);
    await navigator.clipboard.writeText(textValue);
    toast.add({severity: 'success', summary: 'Copied!', detail: 'Value copied to clipboard', life: 2000});
  } catch (err) {
    toast.add({severity: 'error', summary: 'Error', detail: 'Failed to copy value', life: 3000});
  }
};

// Download single row as JSON
const downloadRowItem = (rowData) => {
  try {
    const dataStr = JSON.stringify(rowData, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `row-item-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.add({severity: 'success', summary: 'Downloaded!', detail: 'Row downloaded as JSON', life: 2000});
  } catch (err) {
    toast.add({severity: 'error', summary: 'Error', detail: 'Failed to download row', life: 3000});
  }
};

// Copy single row to clipboard
const copyRowItem = async (rowData) => {
  try {
    const dataStr = JSON.stringify(rowData, null, 2);
    await navigator.clipboard.writeText(dataStr);
    toast.add({severity: 'success', summary: 'Copied!', detail: 'Row copied to clipboard', life: 2000});
  } catch (err) {
    toast.add({severity: 'error', summary: 'Error', detail: 'Failed to copy row', life: 3000});
  }
};

// Open Rich Row Viewer Dialog
const openRowDialog = (rowData, index) => {
  selectedRowData.value = rowData;
  selectedRowIndex.value = index;
  showRowAsJson.value = false;
  showRowDialog.value = true;
};

// Navigate between rows in dialog
const navigateRow = (direction) => {
  const newIndex = selectedRowIndex.value + direction;
  if (newIndex >= 0 && newIndex < modelItems.value.length) {
    selectedRowIndex.value = newIndex;
    selectedRowData.value = modelItems.value[newIndex];
  }
};

// Get all field keys for the selected row
const getRowFieldKeys = computed(() => {
  if (!selectedRowData.value) return [];

  // If show all columns is enabled, return all keys
  if (showAllColumnsInDetail.value) {
    return Object.keys(selectedRowData.value).sort();
  }

  // Otherwise, only show selected columns
  if (!selectedModelFields.value || selectedModelFields.value.length === 0) return [];
  return selectedModelFields.value.filter(field => Object.prototype.hasOwnProperty.call(selectedRowData.value, field));
});

const resetDiscoveryViews = () => {
  showModelView.value = false;
  selectedModel.value = null;
  modelFields.value = [];
  modelItems.value = [];
  modelFieldKeys.value = [];
  modelFieldValues.value = [];
  modelFieldValueStats.value = {};
};

const showValueDetail = (value, valueStats = null) => {
  selectedValueDetail.value = value || 'null';
  selectedValueForActions.value = valueStats;
  showValueDetailDialog.value = true;
};

// Show item detail for mobile (similar to showValueDetail)
const showItemDetail = (item) => {
  selectedItemDetail.value = item;
};


// Define field stats copy options
const fieldStatsCopyOptions = ref([
  {
    label: 'Copy as CSV',
    icon: 'pi pi-copy',
    command: () => {
      copyAllFieldStats('csv');
    }
  },
  {
    label: 'Copy as JSON',
    icon: 'pi pi-copy',
    command: () => {
      copyAllFieldStats('json');
    }
  }
]);

// Define field stats download options
const fieldStatsDownloadOptions = ref([
  {
    label: 'Download as CSV',
    icon: 'pi pi-download',
    command: () => {
      downloadAllFieldStats('csv');
    }
  },
  {
    label: 'Download as JSON',
    icon: 'pi pi-download',
    command: () => {
      downloadAllFieldStats('json');
    }
  }
]);

// Add this function to the script section of FeedDetail.vue
const downloadItems = (format = 'csv') => {
  // Use filtered items if available, otherwise fall back to all items
  const dataToDownload = filteredItems.value.length > 0 ? filteredItems.value : modelItems.value;

  if (!dataToDownload || dataToDownload.length === 0) {
    toast.add({
      severity: 'warn',
      summary: 'No Data',
      detail: 'There are no items to download',
      life: 3000
    });
    return;
  }

  try {
    let content;
    let mimeType;
    let extension;

    // Filter data to only include selected fields
    const processedItems = dataToDownload.map(item => {
      const filteredItem = {};
      selectedModelFields.value.forEach(field => {
        filteredItem[field] = item[field];
      });
      return filteredItem;
    });

    if (format === 'json') {
      // JSON format
      content = JSON.stringify(processedItems, null, 2);
      mimeType = 'application/json';
      extension = 'json';
    } else {
      // CSV format (default)
      let csvContent = '';

      // Add header row
      csvContent += selectedModelFields.value.join(',') + '\n';

      // Add data rows
      processedItems.forEach(item => {
        const row = selectedModelFields.value.map(field => {
          // Handle values that might contain commas or quotes
          let value = item[field] || '';
          // Remove HTML tags if present
          value = value.toString().replace(/<[^>]*>/g, '');
          // Escape quotes and wrap in quotes if contains comma
          if (value.includes(',') || value.includes('"')) {
            value = '"' + value.replace(/"/g, '""') + '"';
          }
          return value;
        });
        csvContent += row.join(',') + '\n';
      });

      content = csvContent;
      mimeType = 'text/csv;charset=utf-8;';
      extension = 'csv';
    }

    // Create a blob and download link
    const blob = new Blob([content], {type: mimeType});
    const link = document.createElement('a');

    // Create a URL for the blob
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedModel.value?.name || 'data'}_items.${extension}`);
    link.style.visibility = 'hidden';

    // Append to document, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error) {
    console.error('Error downloading data:', error);
    toast.add({
      severity: 'error',
      summary: 'Download Failed',
      detail: error.message || 'Error creating download file',
      life: 5000
    });
  }
};

// Add this function for copying items to clipboard
const copyItems = (format = 'json') => {
  // Use filtered items if available, otherwise fall back to all items
  const dataToDownload = filteredItems.value.length > 0 ? filteredItems.value : modelItems.value;

  if (!dataToDownload || dataToDownload.length === 0) {
    toast.add({
      severity: 'warn',
      summary: 'No Data',
      detail: 'There are no items to copy',
      life: 3000
    });
    return;
  }

  try {
    let content;

    // Filter data to only include selected fields
    const processedItems = dataToDownload.map(item => {
      const filteredItem = {};
      selectedModelFields.value.forEach(field => {
        filteredItem[field] = item[field];
      });
      return filteredItem;
    });

    if (format === 'json') {
      // JSON format
      content = JSON.stringify(processedItems, null, 2);
    } else {
      // CSV format (default)
      let csvContent = '';

      // Add header row
      csvContent += selectedModelFields.value.join(',') + '\n';

      // Add data rows
      processedItems.forEach(item => {
        const row = selectedModelFields.value.map(field => {
          // Handle values that might contain commas or quotes
          let value = item[field] || '';
          // Remove HTML tags if present
          value = value.toString().replace(/<[^>]*>/g, '');
          // Escape quotes and wrap in quotes if contains comma
          if (value.includes(',') || value.includes('"')) {
            value = '"' + value.replace(/"/g, '""') + '"';
          }
          return value;
        });
        csvContent += row.join(',') + '\n';
      });

      content = csvContent;
    }

    // Copy to clipboard
    copy(content);

    toast.add({
      severity: 'success',
      summary: 'Copied!',
      detail: `${format.toUpperCase()} data copied to clipboard (${processedItems.length} items)`,
      life: 3000
    });

  } catch (error) {
    console.error('Error copying data:', error);
    toast.add({
      severity: 'error',
      summary: 'Copy Failed',
      detail: error.message || 'Error copying data to clipboard',
      life: 5000
    });
  }
};

// Field Value Stats Copy/Download Functions
const copyFieldValueStat = (fieldValue) => {
  if (fieldValue) {
    const statData = {
      value: fieldValue.value,
      count: fieldValue.count
    };
    copy(JSON.stringify(statData, null, 2));
    toast.add({
      severity: 'info',
      summary: 'Copied',
      detail: 'Field value stat copied to clipboard',
      life: 3000
    });
  }
};

const copyToClipboard = (text) => {
  copy(text);
  toast.add({
    severity: 'info',
    summary: 'Copied',
    detail: 'Value copied to clipboard',
    life: 3000
  });
};

const downloadFieldValueStat = (fieldValue, fieldName) => {
  if (!fieldValue) return;

  try {
    const statData = {
      field: fieldName,
      value: fieldValue.value,
      count: fieldValue.count
    };

    const content = JSON.stringify(statData, null, 2);
    const blob = new Blob([content], {type: 'application/json'});
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${fieldName}_value_stat.json`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.add({
      severity: 'success',
      summary: 'Downloaded',
      detail: 'Field value stat downloaded successfully',
      life: 3000
    });
  } catch (error) {
    console.error('Error downloading field value stat:', error);
    toast.add({
      severity: 'error',
      summary: 'Download Failed',
      detail: error.message || 'Error downloading field value stat',
      life: 5000
    });
  }
};

const copyAllFieldStats = (format = 'json') => {
  if (!modelFieldValues.value || modelFieldValues.value.length === 0) {
    toast.add({
      severity: 'warn',
      summary: 'No Data',
      detail: 'No field value stats to copy',
      life: 3000
    });
    return;
  }

  try {
    let content;
    const fieldName = selectedFieldForValues.value?.name || 'field';

    if (format === 'csv') {
      // CSV format
      let csvContent = 'Value,Count\n';
      modelFieldValues.value.forEach(item => {
        const value = (item.value || '').toString().replace(/"/g, '""');
        csvContent += `"${value}",${item.count}\n`;
      });
      content = csvContent;
    } else {
      // JSON format
      const statsData = {
        field: fieldName,
        totalValues: modelFieldValues.value.length,
        stats: modelFieldValues.value
      };
      content = JSON.stringify(statsData, null, 2);
    }

    copy(content);
    toast.add({
      severity: 'success',
      summary: 'Copied',
      detail: `All field stats copied as ${format.toUpperCase()} (${modelFieldValues.value.length} values)`,
      life: 3000
    });
  } catch (error) {
    console.error('Error copying field stats:', error);
    toast.add({
      severity: 'error',
      summary: 'Copy Failed',
      detail: error.message || 'Error copying field stats',
      life: 5000
    });
  }
};

const downloadAllFieldStats = (format = 'json') => {
  if (!modelFieldValues.value || modelFieldValues.value.length === 0) {
    toast.add({
      severity: 'warn',
      summary: 'No Data',
      detail: 'No field value stats to download',
      life: 3000
    });
    return;
  }

  try {
    let content;
    let mimeType;
    let extension;
    const fieldName = selectedFieldForValues.value?.name || 'field';

    if (format === 'csv') {
      // CSV format
      let csvContent = 'Value,Count\n';
      modelFieldValues.value.forEach(item => {
        const value = (item.value || '').toString().replace(/"/g, '""');
        csvContent += `"${value}",${item.count}\n`;
      });
      content = csvContent;
      mimeType = 'text/csv;charset=utf-8;';
      extension = 'csv';
    } else {
      // JSON format
      const statsData = {
        field: fieldName,
        totalValues: modelFieldValues.value.length,
        stats: modelFieldValues.value
      };
      content = JSON.stringify(statsData, null, 2);
      mimeType = 'application/json';
      extension = 'json';
    }

    const blob = new Blob([content], {type: mimeType});
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${fieldName}_stats.${extension}`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.add({
      severity: 'success',
      summary: 'Downloaded',
      detail: `Field stats downloaded as ${format.toUpperCase()} (${modelFieldValues.value.length} values)`,
      life: 3000
    });
  } catch (error) {
    console.error('Error downloading field stats:', error);
    toast.add({
      severity: 'error',
      summary: 'Download Failed',
      detail: error.message || 'Error downloading field stats',
      life: 5000
    });
  }
};

// Delete functionality
const confirmDelete = () => {
  deleteDialog.value = true;
};

const deleteFeed = async () => {
  if (!source.value || !sourceId) return;

  try {
    deleting.value = true;
    await apiService.deleteSource(sourceId);

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Feed "${source.value.name}" deleted successfully`,
      life: 3000
    });

    // Redirect to feeds listing
    router.push('/feeds');
  } catch (error) {
    console.error('Error deleting feed:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to delete feed: ${error.message}`,
      life: 5000
    });
  } finally {
    deleting.value = false;
    deleteDialog.value = false;
  }
};

// Navigate to feed setup
const navigateToSetup = () => {
  router.push('/feed/setup');
};

// Download file functionality for file type feeds
const downloadFile = async () => {
  if (!editedSource.connection?.url) {
    toast.add({
      severity: 'warn',
      summary: 'No File URL',
      detail: 'No file URL is available for download',
      life: 3000
    });
    return;
  }

  try {
    // Show loading toast
    toast.add({
      severity: 'info',
      summary: 'Downloading File',
      detail: 'Please wait while the file is being downloaded...',
      life: 3000
    });

    // Fetch the file content
    const response = await fetch(editedSource.connection.url);
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    // Get the file as a blob
    const blob = await response.blob();

    // Extract filename from URL or use a default
    const url = editedSource.connection.url;
    const filename = url.split('/').pop() || 'downloaded_file';

    // Create a blob URL and download
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    URL.revokeObjectURL(blobUrl);

    toast.add({
      severity: 'success',
      summary: 'Download Started',
      detail: 'File download has been initiated',
      life: 3000
    });
  } catch (error) {
    console.error('Error downloading file:', error);
    toast.add({
      severity: 'error',
      summary: 'Download Failed',
      detail: `Failed to download file: ${error.message}`,
      life: 5000
    });
  }
};

// Handle file replacement for file type feeds
const handleFileReplace = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file size (10MB max)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    toast.add({
      severity: 'error',
      summary: 'File Too Large',
      detail: 'File size must be less than 10MB',
      life: 5000
    });
    return;
  }

  try {
    // Show loading toast
    toast.add({
      severity: 'info',
      summary: 'Uploading File',
      detail: 'Please wait while the file is being uploaded...',
      life: 3000
    });

    // Use the existing API service to update the file
    const response = await apiService.updateFileSource(sourceId, file);

    // Update the source data with the new file info
    source.value = response.data_source;
    initializeForm();

    toast.add({
      severity: 'success',
      summary: 'File Replaced',
      detail: `File "${file.name}" has been uploaded successfully`,
      life: 3000
    });

    // Reset the file input
    event.target.value = '';

    // Always retrigger parsing when file content changes to ensure data model is up to date
    toast.add({
      severity: 'info',
      summary: 'Updating Data Model',
      detail: 'Refreshing data model with the new file...',
      life: 2000
    });

    // Reset discovery views and reparse regardless of current tab
    resetDiscoveryViews();
    await parseFeedModels();

  } catch (error) {
    console.error('Error replacing file:', error);
    toast.add({
      severity: 'error',
      summary: 'Upload Failed',
      detail: `Failed to upload file: ${error.message}`,
      life: 5000
    });

    // Reset the file input on error
    event.target.value = '';
  }
};

const backToFieldsList = () => {
  displayedModelField.value = '';
  modelFieldValues.value = [];
};

</script>

<style scoped>
/* Modern Design System - FeedDetail */

/* Hero Section - Removed (Using StandardHeader now) */


/* Cell Copy Functionality */
.cell-container {
  position: relative;
  min-height: 32px;
  padding: 4px;
}

.cell-container:hover {
  background-color: #f8fafc;
  border-radius: 4px;
}

.cell-container .cell-content {
  padding-right: 28px; /* Space for copy button */
}

.group:hover .opacity-0 {
  opacity: 1;
}

/* Rich Row Dialog Styles */
.row-dialog .p-dialog-content {
  padding: 0;
}

.row-dialog-content {
  min-height: 400px;
  max-height: 70vh;
  overflow-y: auto;
}

.json-view {
  padding: 1rem;
}

.json-display {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #374151;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
}

.fields-view {
  padding: 1rem;
}

.field-card {
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
}

.field-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.field-card .p-card-header {
  padding: 0;
  border-bottom: 1px solid #e5e7eb;
}

.field-card .p-card-content {
  padding: 0;
}

.field-value {
  min-height: 60px;
  display: flex;
  align-items: flex-start;
}

/* Section Headers */
.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  margin: -1.5rem -1.5rem 1.5rem -1.5rem;
  background: #fafafa;
  border-bottom: 1px solid #e5e7eb;
}

.section-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
}

.section-icon.discovery {
  background: linear-gradient(135deg, #14b8a6, #0d9488);
}

.section-icon.model {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
}

.section-icon.settings {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
}

.section-icon.field-values {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: #111827;
}

.section-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
}

/* Card System */
.discovery-card,
.models-card,
.model-details-card,
.settings-card,
.field-values-card {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  background: white;
  margin-bottom: 1.5rem;
}

.field-values-card {
  border-left: 4px solid #6366f1;
}

/* Simple Card Headers */
.card-header-simple {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem;
  margin: -1.5rem -1.5rem 1.5rem -1.5rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.card-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: white;
}

.card-icon.models {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

/* Models Card Specific */
.models-card {
  width: 100%;
  max-width: 600px;
}

.models-content {
  padding: 0.5rem 0;
}

.models-description {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0 0 1rem 0;
  text-align: left;
}

.models-dropdown {
  width: 100%;
  min-height: 48px;
  font-size: 1rem;
}

:deep(.models-dropdown .p-dropdown) {
  width: 100%;
}

/* Enhanced Model Dropdown Styling */
.model-badge, .option-badge {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  margin-right: 12px;
  flex-shrink: 0;
}

.model-info, .option-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.model-name, .option-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.95rem;
  line-height: 1.2;
  margin-bottom: 2px;
}

.model-path, .option-path {
  color: #6b7280;
  font-size: 0.75rem;
  font-family: 'Courier New', monospace;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-item {
  padding: 8px 0;
  transition: all 0.2s ease;
}

.option-item:hover .option-badge {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
}

.option-item:hover .option-name {
  color: #8b5cf6;
}

.placeholder-text {
  color: #9ca3af;
  font-style: italic;
}

:deep(.models-dropdown .p-dropdown) {
  min-height: 56px;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  transition: all 0.2s ease;
}

:deep(.models-dropdown .p-dropdown:hover) {
  border-color: #d1d5db;
}

:deep(.models-dropdown .p-dropdown.p-focus) {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

/* Compact Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
}

.stat-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  min-height: 80px;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--accent-color);
}

.stat-card.items {
  --accent-color: #3b82f6;
}

.stat-card.fields {
  --accent-color: #10b981;
}

.stat-card.columns {
  --accent-color: #8b5cf6;
}

.stat-card.filters {
  --accent-color: #f59e0b;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px -8px rgba(0, 0, 0, 0.15);
}

.stat-card i {
  font-size: 1rem;
  color: var(--accent-color);
  margin-bottom: 0.25rem;
}

.stat-number {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.125rem;
  display: block;
}

.stat-label {
  font-size: 0.625rem;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Global button enhancements */
:deep(.p-button) {
  transition: all 0.2s ease;
}

:deep(.p-button:hover) {
  transform: translateY(-1px);
}

/* Model Selection Header */
.model-selection-header {
  margin-bottom: 1.5rem;
}


.model-selection-dropdown {
  min-height: 40px;
  min-width: 300px;
  max-width: 500px;
}

.model-badge-small {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  margin-right: 8px;
  flex-shrink: 0;
}

.model-info-compact {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.model-name-compact {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.9rem;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Fields Layout Styles */
.fields-layout {
  height: 600px; /* Fixed height for side-by-side */
}

.field-item {
  transition: all 0.2s ease;
}

.field-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.field-item.selected {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  border-color: #3b82f6;
}

.field-select-btn {
  opacity: 0;
  transform: translateX(-10px);
  transition: all 0.2s ease;
}

.field-item:hover .field-select-btn {
  opacity: 1;
  transform: translateX(0);
}

.values-panel .p-card-content {
  padding: 0 !important;
}

.value-item {
  transition: background 0.15s ease;
}

.value-item:hover {
  background: #f3f4f6 !important;
}

/* Mobile First - FeedDetail optimized for iPhone SE (375x667) */
/* Base mobile styles */
.data-table-column {
  min-width: 8rem; /* Mobile: compact column width */
}

.values-list-container {
  max-height: 70vh;
  overflow-y: auto;
  padding: 0.25rem;
}

.settings-actions {
  margin-top: 1.25rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.test-connection-container {
  order: 2;
  display: flex;
}

.test-connection-btn {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
}

.save-buttons-container {
  order: 1;
  display: flex;
  gap: 0.5rem;
}

.cancel-btn,
.save-btn {
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
}

/* Add Option button styling - Mobile: full width */
.add-option-btn {
  width: 100%;
}

/* Mobile Items List */
.mobile-items-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mobile-item-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-item-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
}

.mobile-item-card:active {
  transform: translateY(1px);
}

.mobile-item-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.mobile-item-primary {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.25;
  word-break: break-word;
}

.mobile-item-secondary {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
  word-break: break-word;
}

.mobile-item-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* Item Detail Panel */
.item-detail-container {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
}

.item-details-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
  width: 100%;
}

.item-detail-field {
  border: 1px solid #f3f4f6;
  border-radius: 0.5rem;
  padding: 0.75rem;
  background: #fafafa;
}

.item-detail-field-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.item-detail-field-value {
  font-size: 0.875rem;
  line-height: 1.5;
  max-width: 100%;
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

/* Mobile-first: Ensure all containers respect viewport boundaries */
@media (max-width: 767px) {
  .item-detail-container,
  .item-details-grid,
  .item-detail-field {
    max-width: calc(100vw - 2rem);
    overflow-x: hidden;
  }

  /* Prevent any card containers from creating extra space */
  .discovery-card .p-card-content {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  /* Ensure mt-2 doesn't cause excessive spacing on mobile */
  .mt-2 {
    margin-top: 0.5rem !important;
  }
}

/* DataTable Header Mobile-First */
.datatable-header-mobile {
  padding: 0.75rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.datatable-header-desktop {
  /* Desktop styles already handled by existing classes */
}

/* ===== SMALL TABLET (576px+) ===== */
@media (min-width: 576px) {
  .data-table-column {
    min-width: 10rem;
  }

  .settings-actions {
    gap: 1rem;
  }

  .cancel-btn,
  .save-btn,
  .test-connection-btn {
    font-size: 1rem;
    padding: 1rem 1.25rem;
  }

  /* Small tablet: mobile items improvements */
  .mobile-item-card {
    padding: 1rem;
  }

  .mobile-item-primary {
    font-size: 1rem;
  }

  .mobile-item-secondary {
    font-size: 0.875rem;
  }

  .item-detail-container {
    padding: 1.25rem;
  }

  .item-details-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* ===== MEDIUM TABLET (768px+) ===== */
@media (min-width: 768px) {
  .data-table-column {
    min-width: 12rem; /* Desktop: wider columns */
  }

  .values-list-container {
    max-height: 400px; /* Desktop: smaller max height */
  }

  .settings-actions {
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e1e1e1;
  }

  .test-connection-container {
    order: 1;
    flex: none;
  }

  .test-connection-btn {
    width: auto;
    padding: 1rem 1.5rem;
    white-space: nowrap;
  }

  .save-buttons-container {
    order: 2;
    flex: none;
    display: flex;
    gap: 0.5rem;
  }

  .cancel-btn,
  .save-btn {
    flex: none;
    width: auto;
    padding: 1rem 1.5rem;
    white-space: nowrap;
  }

  .add-option-btn {
    width: auto !important;
    min-width: 150px;
  }

  /* Medium tablet: item detail improvements */
  .item-details-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .item-detail-field {
    padding: 1rem;
  }

  .item-detail-field-header {
    font-size: 1rem;
  }

  .item-detail-field-value {
    font-size: 1rem;
  }
}

/* ===== LARGE DESKTOP (992px+) ===== */
@media (min-width: 992px) {
  .settings-actions {
    margin-top: 2rem;
    padding-top: 1.25rem;
  }

  .cancel-btn,
  .save-btn,
  .test-connection-btn {
    padding: 1.25rem 2rem;
  }
}

/* Legacy Mobile Responsive - To be refactored */
@media (max-width: 768px) {

  .model-selection-header .flex {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 1rem;
  }

  .model-selection-dropdown {
    min-width: 0;
    width: 100%;
    max-width: 100%;
  }

  /* Mobile fields layout without Card padding */
  .mobile-fields-layout {
    padding: 0; /* Remove extra padding since we removed Card */
  }

  .mobile-fields-layout .field-card {
    margin-left: 0;
    margin-right: 0;
  }

  /* For very small screens (360px and below) */
  @media (max-width: 360px) {
    .model-selection-header .bg-gradient-to-r {
      padding: 0.75rem !important;
    }

    .model-name-compact {
      font-size: 0.8rem !important;
    }

    .model-selection-dropdown {
      font-size: 0.9rem;
    }

    .mobile-fields-layout .field-card {
      margin-bottom: 0.75rem; /* Reduce spacing on very small screens */
    }
  }

  .fields-layout {
    height: auto; /* Remove fixed height on mobile */
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .stat-card {
    padding: 0.5rem;
    min-height: 70px;
  }

  .stat-number {
    font-size: 1.25rem;
  }
}

/* Legacy styles for compatibility */
.field-card {
  background-color: white;
  transition: transform 0.2s, box-shadow 0.2s;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.field-card:active {
  transform: translateY(1px);
}

.field-card-header {
  border-bottom: 1px solid #f0f0f0;
}

.field-card-body {
  background-color: white;
}

.break-all {
  word-break: break-all;
  white-space: normal;
}

/* Modern Tabs */
.modern-tabs {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

:deep(.p-tabview-nav) {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 0;
}

:deep(.p-tabview-header) {
  background: transparent;
  border: none;
  margin-right: 0;
  border-radius: 0;
}

:deep(.p-tabview-nav-link) {
  background: transparent;
  border: none;
  color: #64748b;
  padding: 1rem 1.5rem;
  font-weight: 500;
  border-radius: 0;
  position: relative;
}

:deep(.p-tabview-nav-link:hover) {
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
}

:deep(.p-tabview-header.p-highlight .p-tabview-nav-link) {
  color: #6366f1;
  background: white;
  border-bottom: 2px solid #6366f1;
}

:deep(.p-tabview-panels) {
  background: white;
  padding: 1.5rem;
  border: none;
}

.truncate {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Value Detail Dialog Styles */
.value-detail-dialog .p-dialog-content {
  padding: 0 !important;
}

.value-detail-content {
  padding: 1.5rem;
}

.value-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.value-display {
  background: linear-gradient(145deg, #f8fafc, #f1f5f9);
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.25rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  line-height: 1.6;
  word-break: break-all;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.value-stats {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.stats-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.stat-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.75rem;
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-value {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
}

.dialog-actions {
  display: flex !important;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center !important;
  align-items: center !important;
  width: 100% !important;
}

.dialog-actions-centered {
  justify-content: center !important;
  text-align: center !important;
}

/* Force center alignment for PrimeVue dialog footer */
.value-detail-dialog .p-dialog-footer {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
}

@media (max-width: 640px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .dialog-actions {
    justify-content: stretch;
  }

  .dialog-actions .p-button {
    flex: 1;
  }
}

/* DataTable Header Customization */
:deep(.p-datatable .p-datatable-header) {
  background: #f8fafc !important;
  border-radius: 8px 8px 0 0 !important;
  border-bottom: 1px solid #e5e7eb !important;
  padding: 0.75rem 1rem !important;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: white !important;
  border-bottom: 1px solid #e5e7eb !important;
  color: #374151 !important;
  font-weight: 600 !important;
  padding: 1rem 0.75rem !important;
}

/* Clean breadcrumb styling - target PrimeVue classes */
.breadcrumb-clean :deep(.p-breadcrumb) {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

.breadcrumb-clean :deep(.p-breadcrumb-list) {
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

.breadcrumb-clean :deep(.p-menuitem-link) {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

.breadcrumb-clean :deep(.p-menuitem-separator) {
  background: transparent !important;
}

/* File Action Buttons Styling */
.file-action-button {
  min-width: 120px;
  height: 42px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08);
}

.file-action-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1);
}

.file-action-button:active {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08);
}

.download-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: 1px solid #059669;
  color: white;
}

.download-button:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  border-color: #047857;
  color: white;
}

.download-button:disabled {
  background: #d1d5db;
  border-color: #d1d5db;
  color: #6b7280;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.replace-button {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border: 1px solid #d97706;
  color: white;
}

.replace-button:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  border-color: #b45309;
  color: white;
}

.replace-button:active {
  background: linear-gradient(135deg, #b45309 0%, #92400e 100%);
}

/* Icon spacing in file action buttons */
.file-action-button .p-button-icon {
  margin-right: 0.5rem;
}

.file-action-button .p-button-label {
  font-weight: 500;
}

</style>